/**
 * Owner detail modal — opens from the Owners list on row click.
 *
 * Six tabs mirroring the product spec:
 *   Overview · Invoices · Apartments · Documents · Onboarding · Subscription
 *
 * Data is aggregated by useOwnerDetail(lessor). The Documents tab shows the
 * owner's signed contracts + AGB (read-only) alongside manually uploaded
 * documents, with drag-&-drop upload into the owner_documents store.
 *
 * EVERY owner opens here, including one who only has a portal account. Those
 * rows used to open the "Edit user" form instead — clicking a row gave two
 * different things depending on which half of the record the owner happened to
 * have, and one of them was a form, not a profile. There is no billing record
 * to aggregate for an account-only owner, so they get the reduced profile in
 * OwnerAccountOnlyProfile below: their identity and portal access, stated
 * plainly, plus the one action that fills the gap. Editing stays where it was
 * (the row's ⋯ menu) rather than being what a click does.
 */
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchAdminProfilesDirectory,
  type AdminProfileDirectoryRow,
} from "@/lib/fetch-admin-profiles-directory";
import {
  accountPropertyList,
  findOwnerAccount,
  ownerAccountNameDrift,
} from "@/lib/owner-account-link";
import { isOffsetInvoice } from "@/types/billing";
import type { InvoiceSettlement } from "@/types/billing";
import { inviteOwnerToProperty } from "@/lib/admin-account-actions";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useOwnerEmailHistory } from "@/hooks/v2/useOwnerEmailHistory";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FileText,
  Download,
  Upload,
  Trash2,
  Mail,
  CheckCircle2,
  Clock,
  XCircle,
  Building,
  Building2,
  Pencil,
  Save,
  X,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Loader2,
  ClipboardList,
  Lock,
  Paperclip,
  Link as LinkIcon,
  Info,
  AlertTriangle,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatSupabaseError } from "@/lib/format-supabase-error";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Lessor, LessorInput } from "@/types/lessor";
import { removesBankDetails, useUpdateLessor } from "@/hooks/v2/useLessors";
import { useOperatorBranding } from "@/hooks/useOperatorBranding";
import {
  useManagementInvoices,
  type ManagementInvoiceRow,
} from "@/hooks/v2/useManagementInvoices";
import { OwnerApartmentLink } from "@/components/admin/v2/OwnerApartmentLink";
import {
  ApartmentSearchPicker,
  type PickedApartment,
} from "@/components/admin/v2/ApartmentSearchPicker";
import { NotesDrawer } from "@/components/admin/v2/notes/NotesDrawer";
import { NoteAttachment } from "@/components/admin/v2/notes/note-ui";
import {
  useEntityNotes,
  useOpenNoteCount,
  noteAttachments,
  flattenNotes,
} from "@/hooks/v2/useEntityNotes";
import { OwnerActivityTimeline } from "@/components/admin/v2/OwnerActivityTimeline";
import { buildOwnerActivity } from "@/lib/owner-activity";
import { useOwnerTenancyActivity } from "@/hooks/v2/useOwnerTenancyActivity";
import {
  useManagementInvoiceActions,
  invoicePropertyLabel,
} from "@/components/admin/payments/useManagementInvoiceActions";
import { RemainderInvoiceTag } from "@/components/admin/payments/RemainderInvoiceTag";
import { formatCents } from "@/lib/money";
import {
  splitOwnerInvoices,
  invoiceScheduleWindowEnd,
  invoicedMonthTrend,
  projectOwnerPropertyInvoices,
  groupOwnerInvoicesByProperty,
  type OwnerInvoicePropertyGroup,
  type OwnerInvoiceGroupCounts,
  type OwnerInvoiceRowState,
  type OwnerBillableUnit,
  ownerInvoicePropertyLines,
  ownerInvoicePropertyBreakdown,
  ownerInvoiceBreakdownDrift,
  hasFeeBasisPropertyBreakdown,
  unitMonthlyRevenue,
  fullRentMasterRentUnset,
  type OwnerHeldProperty,
  type NonBillableReasonCode,
} from "@/lib/owner-metrics";
import { logOwnerAudit } from "@/lib/owner-audit";
import {
  liveNetPayoutCentsByInvoiceId,
  payoutToRecordCentsByInvoiceId,
  recordedNetPayoutCents,
} from "@/lib/owner-invoice-net-payout";
import {
  useOwnerDetail,
  useOwnerAuditLogs,
  OWNER_AUDIT_KEY,
  useOwnerDocumentMutations,
  type OwnerDoc,
  type OwnerListRow,
  type OwnerSubmission,
  type OwnerApartment,
  type OnboardingStage,
} from "@/hooks/v2/useOwnerDetail";
import {
  formatEUR,
  formatDate,
  formatAddress,
  maskIban,
  initialsFromName,
} from "@/lib/v2-format";
import { useLanguage } from "@/contexts/LanguageContext";
import { StoredFileLink } from "@/components/shared/StoredFileLink";

const DOC_TYPES: { value: string; label: string }[] = [
  { value: "management_agreement", label: "Management agreement" },
  { value: "proof_of_ownership", label: "Proof of ownership" },
  { value: "payout_statement", label: "Payout statement" },
  { value: "correspondence", label: "Correspondence" },
  { value: "other", label: "Other" },
];

/**
 * The identity fields the profile READS, when there is no billing record to
 * read them from.
 *
 * An account-only owner opens the same profile as everyone else, so the header
 * and Contact card fall back to the account — the only record they have. Kept
 * separate from the editable `current` copy: nothing here is writable, because
 * there is no `lessors` row to write it to.
 */
type OwnerDisplayFields = {
  id?: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city_postal: string | null;
  created_at: string | null;
};

function ownerDisplayFromAccount(
  account: AdminProfileDirectoryRow | null
): OwnerDisplayFields | null {
  if (!account) return null;
  return {
    name: account.name,
    email: account.email,
    phone: account.phone,
    address: account.address,
    // profiles stores one address line; there is no separate postal/city field.
    city_postal: null,
    created_at: account.created_at,
  };
}

function StatusPill({ status }: { status: string | null | undefined }) {
  const s = (status || "").toLowerCase();
  if (s === "verified")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-100">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Verified
      </span>
    );
  if (s === "rejected")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-medium text-rose-700 border border-rose-100">
        <span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> Rejected
      </span>
    );
  if (s === "pending")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 border border-amber-100">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Pending
        review
      </span>
    );
  return null;
}

/**
 * The status of one management invoice.
 *
 * `settlement` matters as much as status here. An OFFSET invoice's fee is
 * deducted from the rent CoLiviq collected, so "Pending" would be a lie — the
 * owner owes nothing and is never chased — and it must never wear the amber
 * "awaiting payment" or red "overdue" colours the dunning states own. It gets
 * its own neutral blue label instead, and once settled it reads "Paid · offset"
 * so a paid invoice still says which way the money went.
 */
function InvoiceStatusPill({
  status,
  settlement,
}: {
  status: string;
  settlement?: InvoiceSettlement | null;
}) {
  const s = status.toLowerCase();
  const offset = isOffsetInvoice({ settlement });
  if (offset && s !== "canceled") {
    const paid = s === "paid";
    return (
      <span
        className={cn(
          "inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium",
          paid
            ? "border-emerald-100 bg-emerald-50 text-emerald-700"
            : "border-sky-100 bg-sky-50 text-sky-700"
        )}
      >
        {paid ? "Paid · offset" : "Offset against rent"}
      </span>
    );
  }
  const cls =
    s === "paid"
      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
      : s === "overdue"
        ? "bg-rose-50 text-rose-700 border-rose-100"
        : s === "canceled"
          ? "bg-muted text-muted-foreground"
          : "bg-amber-50 text-amber-700 border-amber-100";
  return (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${cls}`}
    >
      {status}
    </span>
  );
}

/** How the owner pays the management fee. */
function paymentMethodLabel(
  method: string | null | undefined,
  t: (key: string, fallback: string) => string
): string {
  return method === "sepa"
    ? t("admin.ownerDetail.paymentMethod.sepa", "SEPA direct debit")
    : method === "card" || method === "credit_card"
      ? t("admin.ownerDetail.paymentMethod.card", "Credit card")
      : t("admin.ownerDetail.paymentMethod.deducted", "Deducted from payout");
}

/** "August 2026" from a period_month / date string. */
function monthLabel(
  iso: string | null | undefined,
  language: string = "en"
): string {
  if (!iso) return "—";
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    month: "long",
    year: "numeric",
  });
}

/**
 * "August 2026" for the CURRENT month, read locally.
 *
 * `monthLabel(new Date().toISOString())` would name the UTC month, which in
 * Berlin is the previous one for the first couple of hours of the 1st — while
 * invoicedMonthTrend buckets by the LOCAL month, so the tile would label the
 * wrong month above the right number.
 */
function currentMonthLabel(
  now: Date = new Date(),
  language: string = "en"
): string {
  return now.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    month: "long",
    year: "numeric",
  });
}

/**
 * One onboarding submission, nested in its own collapsible block: an owner can
 * onboard several times (one per apartment/property, or a re-submission after a
 * rejection), so the header names WHICH submission — reference number + the
 * property it covers — and everything belonging to it lives inside.
 */
function OnboardingSubmissionCard({
  submission,
  defaultOpen,
  children,
}: {
  submission: OwnerSubmission;
  defaultOpen: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border bg-card transition-shadow",
        open && "shadow-sm"
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="group flex w-full items-start justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/40"
      >
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-x-2 font-semibold leading-tight">
            <span>Submission {submission.reference_number ?? "—"}</span>
            {submission.propertyTitle && (
              <span className="text-muted-foreground">
                {submission.propertyTitle}
              </span>
            )}
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Submitted {formatDate(submission.submitted_at) ?? "—"}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 self-center">
          <StatusPill status={submission.status} />
          <span className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors group-hover:bg-background">
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform duration-200",
                open && "rotate-180"
              )}
            />
          </span>
        </div>
      </button>
      {open && (
        <div className="space-y-4 border-t bg-muted/20 p-4">{children}</div>
      )}
    </div>
  );
}

/** One metric inside the Management fee card — a soft tile, not a label stack. */
function MetricTile({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl bg-muted/40 p-3.5">
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

/**
 * One number in a card's stat row — label + value, no delta/sparkline (these
 * are running totals, not trend metrics). Proportional (non-tabular) figures
 * per the stat-tile figure contract: tabular-nums is for columns that must
 * align, not a single standalone value.
 */
function MiniStat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0 rounded-xl bg-muted/40 px-3 py-2.5">
      <p className="truncate text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-0.5 truncate text-lg font-semibold tracking-tight">
        {value}
      </p>
    </div>
  );
}

/** One entry in a "how this works" panel: a numbered step, not a wall of text. */
function HelpStep({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3 rounded-xl bg-card px-3 py-2.5 ring-1 ring-border/60">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold tabular-nums text-muted-foreground">
        {n}
      </span>
      <p className="text-xs leading-relaxed text-muted-foreground">
        <span className="font-medium text-foreground">{title}.</span> {children}
      </p>
    </div>
  );
}

/**
 * Collapsible card wrapping one invoice table. Mirrors the tenant rent tab,
 * where the rent schedule and rent history are each their own collapsible
 * section — the chevron flips to mark the open one.
 *
 * `stats`, when given, renders as a small KPI row under the header — always
 * visible, even collapsed, so the headline numbers don't require opening the
 * card to see. `help` adds a second toggle (an info icon) next to the
 * chevron that reveals a plain-language "how this works" panel — collapsed
 * by default, same pattern as the deposit-refund card's guide, so the extra
 * explanation never competes with the numbers for attention.
 */
function CollapsibleTableCard({
  icon: Icon,
  accent,
  title,
  count,
  summary,
  stats,
  help,
  open,
  onToggle,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  title: string;
  count: number;
  summary: React.ReactNode;
  /** Headline numbers shown as a KPI row, regardless of open/closed state. */
  stats?: { label: string; value: React.ReactNode }[];
  /** Plain-language steps shown behind a collapsed "how this works" toggle. */
  help?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const { t } = useLanguage();
  const [showHelp, setShowHelp] = useState(false);
  return (
    <div
      data-testid="collapsible-table-card"
      className={cn(
        "overflow-hidden rounded-2xl border bg-card transition-shadow",
        open && "shadow-sm"
      )}
    >
      <div className="group flex w-full items-center gap-1 pr-2 transition-colors hover:bg-muted/40">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3.5 text-left"
        >
          <span
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1",
              accent
            )}
          >
            <Icon className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <span className="truncate text-sm font-semibold">{title}</span>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
                {count}
              </span>
            </span>
            <span className="mt-0.5 block truncate text-xs text-muted-foreground">
              {summary}
            </span>
          </span>
        </button>
        {help && (
          <button
            type="button"
            onClick={() => setShowHelp((v) => !v)}
            aria-expanded={showHelp}
            aria-label={
              showHelp
                ? t("admin.ownerDetail.helpHide", "Hide how this works")
                : t("admin.ownerDetail.helpShow", "How this works")
            }
            title={t("admin.ownerDetail.helpShow", "How this works")}
            className={cn(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background",
              showHelp && "bg-background text-foreground"
            )}
          >
            <Info className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={onToggle}
          aria-label={
            open
              ? t("admin.ownerDetail.actionCollapse", "Collapse")
              : t("admin.ownerDetail.actionExpand", "Expand")
          }
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors group-hover:bg-background"
        >
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform duration-200",
              open && "rotate-180"
            )}
          />
        </button>
      </div>
      {stats && stats.length > 0 && (
        <div
          className={cn(
            "grid gap-2 border-t px-4 py-3",
            // Three tiles appear on the schedule card as soon as an owner has
            // any offset billing; two elsewhere. Kept as a class switch rather
            // than auto-fit so a two-tile card stays exactly as it was.
            stats.length >= 3 ? "grid-cols-3" : "grid-cols-2"
          )}
        >
          {stats.map((s) => (
            <MiniStat key={s.label} label={s.label} value={s.value} />
          ))}
        </div>
      )}
      {showHelp && help && (
        <div className="space-y-1.5 border-t bg-muted/20 px-4 py-3">{help}</div>
      )}
      {open && <div className="border-t bg-muted/20 p-4">{children}</div>}
    </div>
  );
}

/**
 * Display copy for each `nonBillableReason` code, keyed the same way every
 * other label in this table goes through `t()` — the lib module returns a
 * code, never a display string, precisely so this is the only place English
 * text for it exists.
 */
const NON_BILLABLE_REASON_KEY: Record<NonBillableReasonCode, [string, string]> =
  {
    company_collects: [
      "admin.ownerDetail.reasonCompanyCollects",
      "coliviq collects rent",
    ],
    no_occupancy: ["admin.ownerDetail.reasonNoOccupancy", "No occupied rooms"],
    master_rent_unset: [
      "admin.ownerDetail.reasonMasterRentUnset",
      "Master rent not set",
    ],
    degraded: ["admin.ownerDetail.reasonDegraded", "Fee not calculated"],
  };

/**
 * What ONE invoice row bills, for the group heading and row labels.
 *
 * A projected row carries its unit's title outright; everything real resolves
 * through invoicePropertyLabel, which already prefers the apartment recorded in
 * fee_basis over the building it sits in.
 */
function invoiceUnitTitle(
  inv: ManagementInvoiceRow,
  t?: (key: string, fallback: string) => string
): string {
  if (inv.__unitTitle) return String(inv.__unitTitle);
  return invoicePropertyLabel(inv, t).title;
}

/**
 * True when the row bills a WHOLE PORTFOLIO rather than one unit — a legacy
 * consolidated invoice (fee_basis.level 'lessor', or no unit ids at all).
 *
 * These are the only rows that still expand: their amount is a sum over several
 * properties and the split is the only place those numbers exist. A per-unit
 * invoice has nothing to reveal — its one line would just repeat the row above
 * it, which is exactly the expander this table dropped.
 */
function isPortfolioInvoice(inv: ManagementInvoiceRow): boolean {
  if (inv.__projected) return false;
  if (inv.fee_basis?.level === "lessor") return true;
  return inv.property_id == null && inv.apartment_id == null;
}

/**
 * How many APARTMENT tables a page shows. Lower than the old row-based page
 * size: each group is now a card with its own heading and several months, so
 * five of them is a much taller page than five rows was.
 */
const SCHEDULE_GROUPS_PER_PAGE = 3;

/**
 * How many months ONE apartment's card shows before it pages. Six keeps a
 * card readable at a glance while covering the whole schedule window (three
 * months) plus a half-year of history without a click.
 */
const MONTHS_PER_UNIT_PAGE = 6;

/**
 * How one invoice describes the PROPERTY it belongs to, for grouping.
 *
 * The building names the card whether the row bills the whole property (the
 * shape since 20260911120000) or one flat in it (the per-apartment window),
 * which is what lets an owner's history read continuously across the change.
 * Anything older belongs to the legacy portfolio group, which names no single
 * building.
 */
function describeInvoiceProperty(
  inv: ManagementInvoiceRow,
  t?: (key: string, fallback: string) => string
): {
  name: string;
  propertyTitle: string | null;
  unitName: string | null;
  billable: boolean;
  projected: boolean;
} {
  const billable = inv.__unitBillable !== false;
  const projected = !!inv.__projected;
  const props = Array.isArray(inv.fee_basis?.properties)
    ? (inv.fee_basis!.properties as Array<{
        unit_name?: string;
        apartment_label?: string;
        property_title?: string;
      }>)
    : [];
  // Only a single-unit row names an apartment. A consolidated invoice lists
  // several, and they belong in its expanded breakdown, not the card heading.
  const unitOf = (entries: typeof props): string | null =>
    entries.length === 1
      ? ((entries[0]?.unit_name ?? entries[0]?.apartment_label ?? null) as
          | string
          | null)
      : null;
  // A FORECAST row names its own building outright. It must not fall through
  // to `inv.properties` below: a projection is spread onto the owner's most
  // recent invoice, so that join belongs to the ANCHOR's property and would
  // file the forecast under the wrong building.
  if (inv.__unitName)
    return {
      name: String(inv.__unitName),
      propertyTitle: inv.__unitPropertyTitle ?? null,
      unitName: unitOf(props),
      billable,
      projected,
    };
  if (isPortfolioInvoice(inv))
    return {
      name: invoicePropertyLabel(inv, t).title,
      propertyTitle: null,
      unitName: null,
      billable,
      projected,
    };
  // The join wins over fee_basis: it is the property as it is titled today,
  // while fee_basis froze the title at generation time, and two spellings of
  // one address would open two cards for the same building.
  const name =
    inv.properties?.title ??
    props.find((p) => p.property_title)?.property_title ??
    invoicePropertyLabel(inv, t).title;
  return {
    name,
    propertyTitle: inv.properties?.city ?? null,
    unitName: unitOf(props),
    billable,
    projected,
  };
}

/**
 * Which header chip one invoice counts towards.
 *
 * An unsettled OFFSET invoice is called out separately rather than counted as
 * open: its fee was deducted from rent CoLiviq already holds, so listing it as
 * money owed would report a debt nobody will ever transfer — the same reasoning
 * that keeps it out of `scheduleOpenCents` below.
 */
function invoiceRowState(inv: ManagementInvoiceRow): OwnerInvoiceRowState {
  const status = inv.displayStatus ?? inv.status;
  if (status === "paid") return "paid";
  if (status === "overdue") return "overdue";
  if (isOffsetInvoice(inv)) return "offset";
  return "open";
}

/**
 * The owner's invoices, ONE COLLAPSIBLE CARD PER PROPERTY.
 *
 * A property is billed as one document per month (20260911120000), so the
 * building gets the card: heading, what still needs attention, and its months
 * underneath. A single flat list would interleave buildings — two rows a month
 * apart for the same address ending up nowhere near each other.
 *
 * Shared by the Invoice schedule and Invoice history sections: same columns,
 * same actions, so an admin acts on an open invoice exactly as on a settled one.
 */
function OwnerInvoiceTable({
  groups,
  renderActions,
  netPayoutCents,
  recordPayoutCents,
  heldProperties = [],
  defaultOpen,
}: {
  groups: readonly OwnerInvoicePropertyGroup<ManagementInvoiceRow>[];
  renderActions: (
    inv: ManagementInvoiceRow,
    opts: { ownerStyle: boolean; netPayoutCents?: number | null }
  ) => React.ReactNode;
  /**
   * The row's Net payout in cents, or null when it has none to show. The
   * schedule passes the live payout, the history the confirmed one — see
   * owner-invoice-net-payout.ts.
   */
  netPayoutCents: (inv: ManagementInvoiceRow) => number | null;
  /**
   * What confirming the row's payout will record (COL-516), or null where there
   * is no live figure to record. Only the schedule has one.
   */
  recordPayoutCents: (inv: ManagementInvoiceRow) => number | null;
  /**
   * Every property this owner holds, so a LEGACY portfolio invoice can also
   * list the ones it did not bill. Per-property invoices don't use it — see
   * isPortfolioInvoice.
   */
  heldProperties?: readonly OwnerHeldProperty[];
  /**
   * Whether cards start expanded. True for the schedule (what still needs
   * doing is the reason the tab was opened) and false for the history, which
   * is reference material and would otherwise bury the schedule above it.
   */
  defaultOpen: boolean;
}) {
  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <OwnerInvoicePropertyCard
          key={group.key}
          group={group}
          renderActions={renderActions}
          netPayoutCents={netPayoutCents}
          recordPayoutCents={recordPayoutCents}
          heldProperties={heldProperties}
          defaultOpen={defaultOpen}
        />
      ))}
    </div>
  );
}

/** The header chips that replaced the group's mixed-status money total. */
function OwnerInvoiceGroupChips({
  counts,
}: {
  counts: OwnerInvoiceGroupCounts;
}) {
  const { t } = useLanguage();
  const chips: { key: OwnerInvoiceRowState; label: string; cls: string }[] = [
    {
      key: "overdue",
      label: t("admin.ownerDetail.chipOverdue", "{n} overdue"),
      cls: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300",
    },
    {
      key: "open",
      label: t("admin.ownerDetail.chipOpen", "{n} open"),
      cls: "border-border bg-muted text-foreground",
    },
    {
      key: "offset",
      label: t("admin.ownerDetail.chipOffset", "{n} awaiting offset"),
      cls: "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-300",
    },
    {
      key: "paid",
      label: t("admin.ownerDetail.chipPaid", "{n} paid"),
      cls: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300",
    },
  ];
  const shown = chips.filter((c) => counts[c.key] > 0);
  if (shown.length === 0) return null;
  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {shown.map((c) => (
        <span
          key={c.key}
          className={cn(
            "rounded-full border px-2 py-0.5 text-[11px] font-medium tabular-nums",
            c.cls
          )}
        >
          {c.label.replace("{n}", String(counts[c.key]))}
        </span>
      ))}
    </div>
  );
}

/**
 * One apartment's card, with ITS OWN pagination.
 *
 * Paging lives per card rather than across the tab because the cards are
 * independent lists: an apartment billed since 2024 would otherwise stretch its
 * card down the modal and push every other apartment out of view, and a single
 * shared pager would cut one apartment's months in half to make room for the
 * next one's. Short lists (the usual three-month schedule) render no pager at
 * all — see MONTHS_PER_UNIT_PAGE.
 */
function OwnerInvoicePropertyCard({
  group,
  renderActions,
  netPayoutCents,
  recordPayoutCents,
  heldProperties,
  defaultOpen,
}: {
  group: OwnerInvoicePropertyGroup<ManagementInvoiceRow>;
  renderActions: (
    inv: ManagementInvoiceRow,
    opts: { ownerStyle: boolean; netPayoutCents?: number | null }
  ) => React.ReactNode;
  netPayoutCents: (inv: ManagementInvoiceRow) => number | null;
  recordPayoutCents: (inv: ManagementInvoiceRow) => number | null;
  heldProperties: readonly OwnerHeldProperty[];
  defaultOpen: boolean;
}) {
  const { t } = useLanguage();
  // Whether this building's months are showing. Local, uncontrolled and not
  // persisted: it is a reading aid for one visit to the drawer, and restoring
  // yesterday's fold on a card whose rows have since changed would mislead.
  const [open, setOpen] = useState(defaultOpen);
  // Which rows are expanded to their per-unit breakdown.
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setOpenRows((o) => ({ ...o, [id]: !o[id] }));
  const [page, setPage] = useState(1);
  const totalPages = Math.max(
    1,
    Math.ceil(group.rows.length / MONTHS_PER_UNIT_PAGE)
  );
  // Clamped rather than reset in an effect: the row count changes when an
  // invoice is created or paid, and a page past the end must not render empty.
  const pageSafe = Math.min(page, totalPages);
  const rows = group.rows.slice(
    (pageSafe - 1) * MONTHS_PER_UNIT_PAGE,
    pageSafe * MONTHS_PER_UNIT_PAGE
  );

  return (
    <section
      className="overflow-hidden rounded-xl border bg-card"
      aria-label={group.name}
    >
      {/* The building owns the card, so its name leaves the rows entirely
          — which is also what gives the action cluster room to sit on one
          line instead of stacking into a column.

          The header carries STATUS COUNTS, not a summed amount. A total over a
          card that mixes paid, unsettled and offset months names no quantity
          an admin can act on, and it was the first thing to mislead once the
          card could be collapsed over it. */}
      <header className="border-b bg-muted/30">
        <button
          type="button"
          data-testid="owner-invoice-property-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/50"
        >
          <span className="flex min-w-0 items-center gap-2">
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                !open && "-rotate-90"
              )}
            />
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold leading-tight">
                {group.name}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {[
                  group.propertyTitle,
                  // What the card bills, so a collapsed building still says
                  // whether it covers one flat or six.
                  group.unitNames.length === 1
                    ? t(
                        "admin.ownerDetail.apartmentBilled",
                        "{n} apartment billed"
                      ).replace("{n}", "1")
                    : group.unitNames.length > 1
                      ? t(
                          "admin.ownerDetail.apartmentsBilled",
                          "{n} apartments billed"
                        ).replace("{n}", String(group.unitNames.length))
                      : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
          </span>
          <span className="shrink-0">
            <OwnerInvoiceGroupChips counts={group.counts} />
          </span>
        </button>
      </header>
      {/* Property titles and invoice numbers are wide, so the table scrolls
          sideways instead of pushing the modal out. */}
      <div className="overflow-x-auto" hidden={!open}>
        <Table className="[&_td]:px-3 [&_th]:px-3">
          <TableHeader className="[&_tr]:border-b">
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-8 w-8" />
              <TableHead className="h-8 text-[11px] font-semibold uppercase tracking-wider">
                {t("admin.ownerDetail.colPeriod", "Period")}
              </TableHead>
              <TableHead className="hidden h-8 text-[11px] font-semibold uppercase tracking-wider sm:table-cell">
                {t("admin.ownerDetail.colInvoice", "Invoice")}
              </TableHead>
              <TableHead className="h-8 text-right text-[11px] font-semibold uppercase tracking-wider">
                {t("admin.ownerDetail.colColiviqFee", "coliviq fee")}
              </TableHead>
              <TableHead className="h-8 text-right text-[11px] font-semibold uppercase tracking-wider">
                {t("admin.ownerDetail.colNetPayout", "Net payout")}
              </TableHead>
              <TableHead className="h-8 text-[11px] font-semibold uppercase tracking-wider">
                {t("admin.ownerDetail.colStatus", "Status")}
              </TableHead>
              <TableHead className="h-8 text-right text-[11px] font-semibold uppercase tracking-wider">
                {t("admin.ownerDetail.colAction", "Action")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((inv) => {
              const unitTitle = invoiceUnitTitle(inv, t);
              // A LEGACY portfolio invoice is the only row that still lists
              // the owner's other properties as "not billed": its one
              // document was supposed to cover them all, so an omission
              // there is a gap. A per-unit invoice covers exactly one unit
              // by design and every sibling has its own card, so appending
              // them here would flag already-billed apartments as billing
              // gaps — the very confusion this column was added to end.
              const includeHeldProperties =
                isPortfolioInvoice(inv) &&
                hasFeeBasisPropertyBreakdown(inv.fee_basis);
              const breakdown = ownerInvoicePropertyBreakdown(
                ownerInvoicePropertyLines(
                  inv.fee_basis,
                  inv.amount_cents,
                  unitTitle === "—"
                    ? t("admin.ownerDetail.feeLineFallback", "Management fee")
                    : unitTitle,
                  inv.property_id ?? null
                ),
                includeHeldProperties ? heldProperties : []
              );
              // Non-zero when the recorded per-property fees no longer add
              // up to the invoice total — an edited amount left the
              // original month's split behind. Shown, not smoothed away.
              const drift = ownerInvoiceBreakdownDrift(
                breakdown,
                inv.amount_cents
              );
              // ANY multi-line invoice expands, not just a portfolio one:
              // since 20260911120000 a property's invoice itemises the
              // apartments it bills, and that itemisation is where the
              // per-flat figures now live. A single-unit row still has
              // nothing to reveal — its one line repeats the row above it.
              //
              // Drift is independent of line count: cancel-and-reissue can
              // hand a replacement an amount_cents its inherited fee_basis
              // does not foot, so even a one-line invoice can disagree with
              // its recorded fee and needs somewhere to say so.
              const expandable = breakdown.length > 1 || drift !== 0;
              const rowOpen = expandable && !!openRows[inv.id];
              // A projected row for a property the generator will NOT bill.
              // Real invoices are billable by definition — they exist.
              const billableRow = inv.__unitBillable !== false;
              const netPayout = netPayoutCents(inv);

              return (
                <Fragment key={inv.id}>
                  <TableRow className="transition-colors">
                    <TableCell className="py-2 pr-0">
                      {expandable ? (
                        <button
                          type="button"
                          onClick={() => toggle(inv.id)}
                          aria-expanded={rowOpen}
                          aria-label={
                            rowOpen
                              ? t(
                                  "admin.ownerDetail.actionCollapse",
                                  "Collapse"
                                )
                              : t("admin.ownerDetail.actionExpand", "Expand")
                          }
                          className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
                        >
                          <ChevronDown
                            className={cn(
                              "h-4 w-4 transition-transform duration-200",
                              rowOpen && "rotate-180"
                            )}
                          />
                        </button>
                      ) : null}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "whitespace-nowrap font-medium",
                        !billableRow && "text-muted-foreground"
                      )}
                    >
                      {formatDate(inv.period_month)?.replace(/^\d+ /, "") ??
                        inv.period_month}
                      {/* What this one document covers. A legacy portfolio
                          invoice counts PROPERTIES (no card heading can say
                          it, since it belongs to no single building); a
                          consolidated invoice counts the APARTMENTS folded
                          into it, which is the number that explains why the
                          amount is larger than one flat's fee. */}
                      {breakdown.length > 1 && (
                        <span
                          className="ml-2 text-xs font-normal text-muted-foreground"
                          title={invoicePropertyLabel(inv, t).full ?? undefined}
                        >
                          {breakdown.length}{" "}
                          {isPortfolioInvoice(inv)
                            ? t("admin.ownerDetail.properties", "properties")
                            : t("admin.ownerDetail.apartments", "apartments")}
                        </span>
                      )}
                      {/* COL-483: a remainder shares its month with the
                          invoice it follows — say so, or it reads as a
                          duplicate. */}
                      <RemainderInvoiceTag row={inv} className="ml-2" />
                    </TableCell>
                    <TableCell className="hidden whitespace-nowrap font-mono text-xs text-muted-foreground sm:table-cell">
                      {inv.invoice_no ?? "—"}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "whitespace-nowrap text-right font-medium tabular-nums",
                        !billableRow && "text-muted-foreground/70"
                      )}
                    >
                      {formatCents(inv.amount_cents, inv.currency)}
                    </TableCell>
                    {/* What the owner receives: the live payout on the
                        schedule, what the confirmation paid out in the
                        history. "—" where the invoice has no payout to show
                        (owner-invoice-net-payout.ts says when). */}
                    <TableCell
                      className={cn(
                        "whitespace-nowrap text-right tabular-nums",
                        netPayout === null
                          ? "text-muted-foreground"
                          : "font-medium",
                        !billableRow && "text-muted-foreground/70"
                      )}
                    >
                      {netPayout === null
                        ? "—"
                        : formatCents(netPayout, inv.currency)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {/* A unit that earns nothing gets a row of its own
                              now rather than a line inside an expander, so the
                              reason it is not billed travels with it. */}
                      {billableRow ? (
                        <InvoiceStatusPill
                          status={inv.displayStatus ?? inv.status}
                          settlement={inv.settlement}
                        />
                      ) : inv.__unitReason ? (
                        <Badge
                          variant="outline"
                          className="whitespace-nowrap text-[11px] font-normal text-muted-foreground"
                        >
                          {t(...NON_BILLABLE_REASON_KEY[inv.__unitReason])}
                        </Badge>
                      ) : (
                        // owner_collects + occupied + still nothing to
                        // bill. That is a billing gap, not a
                        // configuration — say so.
                        <Badge
                          variant="outline"
                          className="whitespace-nowrap border-amber-500/40 text-[11px] font-normal text-amber-700 dark:text-amber-400"
                        >
                          {t(
                            "admin.ownerDetail.feeLineUnbilled",
                            "Billable but not invoiced"
                          )}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="py-2 text-right">
                      {/* One kebab per row, billable or not: a non-billable
                          forecast row's menu holds only Payment configuration
                          (see renderInvoiceMenu), since the reason it is not
                          billed usually IS a setting — the switch, the master
                          rent, the model. The status pill already names it. */}
                      <div className="flex flex-nowrap items-center justify-end gap-1.5">
                        {renderActions(inv, {
                          ownerStyle: true,
                          netPayoutCents: recordPayoutCents(inv),
                        })}
                      </div>
                    </TableCell>
                  </TableRow>
                  {/* Read-only detail: a portfolio row's multi-property
                      split, or any row's drift warning. */}
                  {rowOpen &&
                    breakdown.map((line, i) => (
                      <TableRow
                        // Every line of a consolidated invoice shares one
                        // propertyId, so the apartment has to be in the key —
                        // otherwise React sees duplicates down the breakdown.
                        key={`${inv.id}-p${line.propertyId ?? "x"}-a${line.apartmentId ?? i}`}
                        className="bg-muted/20 hover:bg-muted/20"
                      >
                        <TableCell className="py-2" />
                        <TableCell className="py-2 text-sm text-muted-foreground">
                          {line.title}
                        </TableCell>
                        <TableCell className="hidden py-2 sm:table-cell" />
                        <TableCell
                          className={cn(
                            "py-2 text-right text-sm tabular-nums",
                            line.billable
                              ? "text-muted-foreground"
                              : "text-muted-foreground/60"
                          )}
                        >
                          {formatCents(line.feeCents, inv.currency)}
                        </TableCell>
                        {/* Net payout is a per-invoice figure: the lines
                            below it split the fee, not the payout. */}
                        <TableCell className="py-2" />
                        <TableCell className="py-2" colSpan={2}>
                          {line.billable ? (
                            <span className="text-xs text-muted-foreground">
                              {t(
                                "admin.ownerDetail.feeLineBilled",
                                "Billed this month"
                              )}
                            </span>
                          ) : line.reason ? (
                            <Badge
                              variant="outline"
                              className="whitespace-nowrap text-[11px] font-normal text-muted-foreground"
                            >
                              {t(...NON_BILLABLE_REASON_KEY[line.reason])}
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="whitespace-nowrap border-amber-500/40 text-[11px] font-normal text-amber-700 dark:text-amber-400"
                            >
                              {t(
                                "admin.ownerDetail.feeLineUnbilled",
                                "Billable but not invoiced"
                              )}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  {rowOpen && drift !== 0 && (
                    <TableRow className="bg-muted/20 hover:bg-muted/20">
                      <TableCell className="py-2" />
                      <TableCell
                        className="py-2 text-xs text-amber-700 dark:text-amber-400"
                        colSpan={6}
                      >
                        {t(
                          "admin.ownerDetail.feeBreakdownDrift",
                          "This invoice's amount was changed after it was calculated — the properties below add up to"
                        )}{" "}
                        {formatCents(inv.amount_cents - drift, inv.currency)}.
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {/* The card's own pager, inside its border so it reads as this
          building's months rather than the tab's. Hidden with the rows it
          pages: a pager over a collapsed card controls nothing visible. */}
      {open && group.rows.length > MONTHS_PER_UNIT_PAGE && (
        <div className="border-t px-3 pb-2">
          <Pager
            page={pageSafe}
            totalPages={totalPages}
            onPage={setPage}
            total={group.rows.length}
            pageSize={MONTHS_PER_UNIT_PAGE}
          />
        </div>
      )}
    </section>
  );
}

/** Small labelled key/value row used in the Overview + Onboarding cards. */
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right">{value ?? "—"}</span>
    </div>
  );
}

/** Row that becomes an input in edit mode; falls back to `display` for reading. */
function EditRow({
  label,
  editing,
  value,
  onChange,
  mono,
  display,
}: {
  label: string;
  editing: boolean;
  value: string | null | undefined;
  onChange: (v: string) => void;
  mono?: boolean;
  display?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <span className="text-sm text-muted-foreground">{label}</span>
      {editing ? (
        <Input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={`h-8 max-w-[60%] text-right ${mono ? "font-mono" : ""}`}
        />
      ) : (
        <span
          className={`text-sm font-medium text-right ${mono ? "font-mono" : ""}`}
        >
          {display ?? value ?? "—"}
        </span>
      )}
    </div>
  );
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border bg-card p-5">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
        {title}
      </p>
      {children}
    </div>
  );
}

/**
 * Fourth slot of the overview grid, right of Representor and under Bank &
 * payment — same SectionCard idiom as its neighbors (rounded-2xl border
 * bg-card p-5), collapsible the way the tenant profile's Email history card
 * is. Data comes from useOwnerEmailHistory, whose "sent" evidence is the real
 * owner_lifecycle_emails send-log (20260826160000) — see that hook's header
 * for why the *_owner_email_sent_at stamp columns must never be read as send
 * evidence directly.
 */
function OwnerEmailHistoryCard({
  lessorId,
  apartmentIds,
}: {
  lessorId: string | null | undefined;
  apartmentIds: Array<number | null>;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const {
    data: entries,
    isLoading,
    isError,
  } = useOwnerEmailHistory(lessorId, apartmentIds);
  const sentCount = (entries ?? []).filter((e) => e.sent).length;
  // Counted apart from pending on purpose: these are mails the log says
  // definitively did NOT go out (20260903140000), and folding them into
  // "pending" is what let a failed owner notification read as one still on
  // its way. The summary line is where someone notices there is anything to
  // chase at all, so the number has to appear there, not only in the rows.
  const failedCount = (entries ?? []).filter(
    (e) => !!e.notDeliveredReason
  ).length;
  const pendingCount = (entries ?? []).length - sentCount - failedCount;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-2xl border bg-card p-5"
    >
      <CollapsibleTrigger className="flex w-full items-start justify-between gap-3 text-left">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">
            {t("admin.ownerDetail.sectionEmailHistory", "Email history")}
          </p>
          <p className="text-xs text-muted-foreground">
            {isLoading
              ? t("admin.ownerDetail.loading", "Loading…")
              : isError
                ? t("admin.ownerDetail.couldNotLoad", "Could not be loaded")
                : [
                    `${sentCount} ${t("admin.ownerDetail.emailSent", "sent")}`,
                    failedCount
                      ? `${failedCount} ${t("admin.ownerDetail.emailNotDelivered", "not delivered")}`
                      : null,
                    `${pendingCount} ${t("admin.ownerDetail.emailPending", "pending")}`,
                    t("admin.ownerDetail.emailNewestFirst", "newest first"),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
          </p>
        </div>
        <ChevronDown
          className={cn(
            "mt-0.5 h-4 w-4 flex-none text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3 space-y-0.5 border-t pt-3">
        {isLoading ? null : isError ? (
          /**
           * Deliberately not "No owner emails on record yet": the send-log read
           * is what failed (useOwnerEmailHistory throws rather than degrading
           * it to an empty list), and this card's whole point is that it never
           * asserts anything about a mail it cannot actually verify.
           */
          <p className="py-2 text-sm text-muted-foreground">
            {t(
              "admin.ownerDetail.emailHistoryError",
              "The email send-log could not be read, so no history can be shown."
            )}
          </p>
        ) : !entries?.length ? (
          <p className="py-2 text-sm text-muted-foreground">
            {t(
              "admin.ownerDetail.noOwnerEmails",
              "No owner emails on record yet."
            )}
          </p>
        ) : (
          entries.map((e, i) => (
            <div
              key={`${e.title}-${i}`}
              className="flex items-start gap-3 border-b py-2.5 last:border-b-0"
            >
              <div
                className={cn(
                  "mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full",
                  e.sent
                    ? "bg-emerald-50 text-emerald-600"
                    : e.notDeliveredReason
                      ? "bg-amber-50 text-amber-600"
                      : "bg-muted text-muted-foreground"
                )}
              >
                {e.sent ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : e.notDeliveredReason ? (
                  <AlertTriangle className="h-3.5 w-3.5" />
                ) : (
                  <Clock className="h-3.5 w-3.5" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{e.title}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {e.desc}
                </p>
                {/* Who recorded the event, on its own line so the truncated
                    desc above cannot swallow it — an owner's first question
                    about a rent month nobody paid online is who says it was
                    paid, and the answer is an admin's name. */}
                {e.attribution ? (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {e.attribution}
                  </p>
                ) : null}
                {/* The reason, not just the fact. "Not delivered" alone sends
                    someone hunting through logs; the cause is what tells them
                    whether to add an address or leave a muted owner alone. */}
                {e.notDeliveredReason ? (
                  <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-500">
                    {e.notDeliveredReason}
                  </p>
                ) : null}
              </div>
              <div className="flex-none text-right">
                <Badge
                  variant="outline"
                  className={cn(
                    "text-[10px]",
                    e.sent
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : e.notDeliveredReason
                        ? "border-amber-200 bg-amber-50 text-amber-700"
                        : "border-muted-foreground/20 bg-muted text-muted-foreground"
                  )}
                >
                  {e.sent
                    ? "Sent"
                    : e.notDeliveredReason
                      ? t(
                          "admin.ownerDetail.emailNotDelivered",
                          "Not delivered"
                        )
                      : "Pending"}
                </Badge>
                {e.ts ? (
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    {formatDate(e.ts)}
                  </p>
                ) : null}
              </div>
            </div>
          ))
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

/** Compact KPI tile for the Overview portfolio strip. */
function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border bg-card p-4 transition-colors hover:border-border">
      <div className="min-w-0">
        <div className="truncate text-xl font-semibold leading-none tabular-nums text-foreground">
          {value}
        </div>
        <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
      </div>
      {hint && <p className="mt-2 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function OwnerDetailModal({
  lessor,
  account: accountRow = null,
  propertiesById,
  onAddBillingEntity,
  onClose,
  headerActions,
}: {
  lessor: Lessor | null;
  /**
   * The portal account the CALLER already resolved for this owner (the Owners
   * list does that join for every row). Passing it is what lets an owner who
   * has only a login open a profile at all — with no `lessor` there is nothing
   * for the email match below to work from. Optional: callers that only ever
   * hold a billing record (property / apartment detail) keep working unchanged.
   */
  account?: AdminProfileDirectoryRow | null;
  /** Property titles by id — labels the account's assignments when there is no billing record. */
  propertiesById?: Map<number, { title: string | null; city: string | null }>;
  /** Opens the caller's "add the missing billing entity" flow. */
  onAddBillingEntity?: () => void;
  onClose: () => void;
  /**
   * Extra controls for the header's top-right cluster, rendered before the
   * notes/expand buttons (e.g. the Owners table's "…" row actions).
   */
  headerActions?: React.ReactNode;
}) {
  const { t, language } = useLanguage();
  const { data, isLoading } = useOwnerDetail(lessor);
  // Operators see their own company name in fee copy (COL-352 Phase 5).
  const { platformName, isOperator } = useOperatorBranding();
  const { upload, remove } = useOwnerDocumentMutations(lessor);
  // Invoices come from the SAME list the Payments hub renders, so the Invoices
  // tab offers the identical actions (view invoice, proof, note, mark paid).
  const { data: allInvoices = [], isLoading: invoicesLoading } =
    useManagementInvoices({ enabled: !!lessor });
  const { renderActions, overlays } = useManagementInvoiceActions();
  const updateMut = useUpdateLessor();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState("overview");
  const [expanded, setExpanded] = useState(false);
  // Internal notes, over the modal rather than as an eighth tab — the same
  // decision (and the same feed) as the tenant profile: notes are what you want
  // open WHILE reading the invoices or the onboarding.
  const [notesOpen, setNotesOpen] = useState(false);
  const ownerNoteScope = lessor?.id
    ? ({ level: "owner", lessorId: lessor.id } as const)
    : null;
  const openNoteCount = useOpenNoteCount(ownerNoteScope);
  // The notes feed is already fetched for the badge above, so lifting the
  // attachments out of it costs nothing — no second request for the Documents
  // tab or the Activity entries.
  const { data: notesFeed } = useEntityNotes(ownerNoteScope);
  const noteFiles = useMemo(
    () => noteAttachments(notesFeed.threads),
    [notesFeed.threads]
  );
  // The Activity tab takes the whole feed, not just the files: a written note
  // is as much a thing that happened as an uploaded one.
  const noteEvents = useMemo(
    () => flattenNotes(notesFeed.threads),
    [notesFeed.threads]
  );
  const {
    data: auditLogs = [],
    isLoading: auditLoading,
    isError: auditFailed,
  } = useOwnerAuditLogs(lessor?.id ?? null);
  // Separate pagers for the two invoice sections (schedule vs history). The
  // schedule opens by default — it holds what still needs acting on; the
  // settled history is one click away.
  const [schedOpen, setSchedOpen] = useState(true);
  const [histOpen, setHistOpen] = useState(false);
  const [schedPage, setSchedPage] = useState(1);
  const [invPage, setInvPage] = useState(1);
  const [aptPage, setAptPage] = useState(1);
  const [docType, setDocType] = useState("other");
  const [notes, setNotes] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [resending, setResending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Local, editable copy of the owner. Edits save to the lessors table (the
  // single source of truth) and optimistically update this copy so the modal
  // reflects them immediately; the list refetches via query invalidation.
  const [current, setCurrent] = useState<Lessor | null>(lessor);
  const [isEditing, setIsEditing] = useState(false);
  // Pending save that removes payout details, held until a reason is given.
  // Keeps the built payload so the confirmation writes exactly what was
  // reviewed, not whatever the form holds by the time it is confirmed.
  const [bankReason, setBankReason] = useState<{
    input: LessorInput;
    text: string;
  } | null>(null);

  // Reset the modal's transient UI only when a DIFFERENT owner is opened.
  //
  // Keyed on the id, not the object: the list now re-hands this component a
  // fresh row on every refetch (so the modal stops showing pre-save values),
  // and keying on identity would make a background refetch — a window focus, an
  // invalidation from the Payments hub — slam the admin back to the Overview
  // tab and cancel an edit they were halfway through typing.
  //
  // An account-only owner has no `lessors` row, so `lessor?.id` alone is
  // `null` for every one of them — falling back to the account id keeps two
  // different account-only owners distinguishable, while a background
  // refetch of the SAME account still holds this steady (its id doesn't
  // change).
  const resetKey = lessor?.id ?? accountRow?.id ?? null;
  useEffect(() => {
    setIsEditing(false);
    setTab("overview");
    setSchedOpen(true);
    setHistOpen(false);
    setSchedPage(1);
    setInvPage(1);
    setAptPage(1);
    // Otherwise the next owner opens with the previous owner's notes panel
    // already sitting over their modal.
    setNotesOpen(false);
    // Unconditional, unlike the re-seed effect below: switching to a
    // DIFFERENT owner must always discard whatever was mid-edit on the
    // previous one, same as the isEditing/tab reset above. Without this, a
    // same-render owner switch (e.g. LessorsListPage's ?owner= deep-link
    // firing while another owner's edit is in progress) reads isEditingRef
    // as still true — it hasn't caught up to the setIsEditing(false) above
    // yet — so the [lessor]-keyed effect skips its update, and `current`
    // (and every editable field) stays stuck on the old owner indefinitely.
    // Deliberately keyed on resetKey, not lessor/accountRow: a background
    // refetch that hands down a new object for the SAME id must not re-run
    // this.
    setCurrent(lessor);
    // A pending "why are you removing payout details" prompt is scoped to the
    // owner it was raised for — carrying it over to a different owner would
    // confirm a removal against the wrong record.
    setBankReason(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Re-seed the editable copy when the list hands down a NEW row — except
  // mid-edit, where the admin's typing is the more recent truth and
  // overwriting it would discard their unsaved work.
  //
  // `isEditing` is read through a ref so it stays out of the dependency list.
  // As a dependency it would re-run this the instant a save flips editing off,
  // and at that moment the refetch it triggered has not landed yet — `lessor`
  // is still the pre-save row, so the just-saved values would visibly flash
  // back to their old ones before correcting themselves a beat later.
  const isEditingRef = useRef(isEditing);
  isEditingRef.current = isEditing;
  useEffect(() => {
    if (isEditingRef.current) return;
    setCurrent(lessor);
  }, [lessor]);

  // The portal account behind this owner. Shown so the two records an owner is
  // stored under stop being invisible to each other: an admin in the Owners
  // area can see whether the person also has a login, and under which name.
  // Shares the ["admin-role-mgmt-users"] cache the Owners list already fills,
  // so opening a profile costs no extra request.
  const { data: accounts } = useQuery({
    queryKey: ["admin-role-mgmt-users"],
    enabled: !!lessor,
    queryFn: fetchAdminProfilesDirectory,
  });
  const matchedAccount = useMemo(
    () => findOwnerAccount(current ?? lessor, accounts),
    [current, lessor, accounts]
  );
  // The caller's already-joined account wins: the Owners list matches on the
  // stored property_owners.lessor_id link first, which survives an owner
  // changing their email address — the fallback here can only match on email.
  const account = accountRow ?? matchedAccount;
  /**
   * Whether a `lessors` row backs this owner.
   *
   * An owner who only has a portal account opens THIS profile, not a different
   * screen — the Owners list used to send them to the account edit form, which
   * made a row click mean two different things. Everything the tabs aggregate
   * keys off the billing row though, so where there is none the surfaces below
   * say so instead of rendering an unverified zero, and the writes (edit, notes,
   * document upload) stay closed: there is nothing to write them to.
   */
  const hasBilling = !!lessor;
  /** Identity for display: the owner record, falling back to the account. */
  const shown = current ?? ownerDisplayFromAccount(accountRow);
  // Non-null only when the account and the owner record disagree on the name of
  // the same person — the drift this release exists to end. Company/GbR records
  // are exempt: there the two names are different things by design.
  const accountNameDrift = ownerAccountNameDrift({
    ownerName: current?.name,
    ownerType: current?.type,
    accountName: account?.name,
  });

  // "+ Link apartment" — lets an admin attribute one more existing apartment
  // to this owner's portal account, additively (owners can hold several
  // apartments, even in the same building). Needs the portal ACCOUNT id
  // (apartments.owner_user_id is a user id, not a lessor id), so the action
  // is unavailable for an owner who only has a billing record and no login.
  const [linkApartmentOpen, setLinkApartmentOpen] = useState(false);
  const [linkingApartment, setLinkingApartment] = useState(false);

  const linkApartment = useCallback(
    async (picked: PickedApartment | null) => {
      if (!picked || !account?.id) return;
      setLinkingApartment(true);
      try {
        const { error } = await supabase.rpc("admin_link_owner_apartment", {
          _user_id: account.id,
          _apartment_id: picked.id,
        });
        if (error) throw error;
        toast.success(
          t("admin.ownerDetail.apartmentLinked", "Apartment linked.")
        );
        // invalidateOwnerCaches doesn't cover this key (see its own doc
        // comment) — this modal's own `account`/`matchedAccount` and the
        // Owners list both read it directly, same as OwnerActionsMenu's
        // `refetch` invalidates both together for the same reason.
        queryClient.invalidateQueries({ queryKey: ["admin-role-mgmt-users"] });
        invalidateOwnerCaches(queryClient, lessor?.id ?? null);
        setLinkApartmentOpen(false);
      } catch (e: unknown) {
        toast.error(
          formatSupabaseError(e) ||
            t(
              "admin.ownerDetail.errApartmentLinkFailed",
              "Could not link the apartment."
            )
        );
      } finally {
        setLinkingApartment(false);
      }
    },
    [account?.id, lessor?.id, queryClient, t]
  );

  const PAGE_SIZE = 5;
  // This owner's management invoices, straight off the Payments-hub list so
  // both surfaces show the same rows (and the same actions operate on them).
  const invoices = useMemo(
    () =>
      lessor ? allInvoices.filter((r) => r.lessors?.id === lessor.id) : [],
    [allInvoices, lessor]
  );
  // Memoised, not `data?.apartments ?? []`: the fallback allocates a fresh
  // array on every render, which re-ran every downstream useMemo — including
  // the one that builds the invoice forecast, handing the table new row
  // objects each time.
  const apartments = useMemo(() => data?.apartments ?? [], [data?.apartments]);
  // Rooms in the owner's properties that aren't assigned to any apartment.
  // generate_management_invoices bills these too, off the PROPERTY's own
  // collection_mode/revenue_model (apartment_id IS NULL branch) — so they
  // must count toward a property's held/billable status exactly like an
  // onboarded apartment does, or a property whose only presence is
  // unassigned rooms can vanish from the breakdown the same way this PR's
  // reported bug did.
  const unassignedUnits = useMemo(
    () => data?.unassignedUnits ?? [],
    [data?.unassignedUnits]
  );
  // The owner's portfolio rolled up to PROPERTY level — the granularity the
  // management invoice is billed at (one fee line per property, see
  // generate_management_invoices). Feeds the invoice breakdown so a property
  // that earned no fee this month is still listed, with the reason it earned
  // none, instead of silently vanishing from the schedule.
  const heldProperties = useMemo<OwnerHeldProperty[]>(() => {
    // Per-UNIT generator fee first, then rolled up — not "any owner_collects
    // unit" and "any occupied unit" tracked independently. Doing it
    // independently let facts from DIFFERENT units cross-contaminate: an
    // occupied company_collects apartment plus a vacant owner_collects
    // sibling would read as "owner_collects" AND "occupied", i.e. billable,
    // though the generator bills neither. Computing the actual fee per unit
    // (mirroring generate_management_invoices' own model math via
    // unitMonthlyRevenue, fed by the GENERATOR's occupancy reading — not the
    // booking-derived one the rest of this modal shows) also folds in a
    // legitimately-zero fee config (e.g. a 0% rate): an occupied owner_collects
    // unit whose fee still computes to zero is exactly as unbillable to the
    // generator as an empty one, so it must not read as a real gap either.
    const byProperty = new Map<
      number,
      OwnerHeldProperty & { anyOwnerCollects: boolean }
    >();
    for (const a of [...apartments, ...unassignedUnits]) {
      const config = {
        revenue_model: a.revenue_model,
        coliviq_fee_pct: a.coliviq_fee_pct,
        coliviq_fee_amount: a.coliviq_fee_amount,
        master_rent: a.master_rent,
      };
      const ownerCollects = a.collection_mode === "owner_collects";
      const unitFee = ownerCollects
        ? unitMonthlyRevenue(
            config,
            a.generator_occupied_rooms,
            a.generator_occupied_rent
          )
        : 0;
      // An occupied full_rent unit the generator skips for want of a master
      // rent — the reason to show is that, not "no occupied rooms".
      const unitMasterUnset =
        ownerCollects &&
        a.generator_occupied_rooms > 0 &&
        fullRentMasterRentUnset(config);
      const prev = byProperty.get(a.property_id);
      if (!prev) {
        byProperty.set(a.property_id, {
          propertyId: a.property_id,
          title: a.property_title ?? `Property ${a.property_id}`,
          collectionMode: ownerCollects ? "owner_collects" : "company_collects",
          hasOccupancy: unitFee > 0,
          degraded: a.rooms_degraded,
          masterRentUnset: unitMasterUnset,
          anyOwnerCollects: ownerCollects,
        });
        continue;
      }
      // Apartments in one building can differ. The reason shown must not be
      // contradicted by a sibling apartment: the property counts as
      // owner_collects (billable in principle) once ANY unit is, and as
      // occupied/fee-earning once ANY unit's OWN fee is positive.
      prev.hasOccupancy = prev.hasOccupancy || unitFee > 0;
      prev.degraded = prev.degraded || a.rooms_degraded;
      prev.masterRentUnset = prev.masterRentUnset || unitMasterUnset;
      prev.anyOwnerCollects = prev.anyOwnerCollects || ownerCollects;
      prev.collectionMode = prev.anyOwnerCollects
        ? "owner_collects"
        : "company_collects";
    }
    return Array.from(byProperty.values()).map(
      ({ anyOwnerCollects: _anyOwnerCollects, ...p }) => p
    );
  }, [apartments, unassignedUnits]);
  // The properties this owner's ACCOUNT is assigned to — the same resolution
  // the Owners row uses for its property tags. Only ever shown when there is no
  // billing record; with one, the Apartments tab lists the real portfolio.
  const accountProperties = useMemo(
    () => accountPropertyList(accountRow, propertiesById),
    [accountRow, propertiesById]
  );
  const aptTotalPages = Math.max(1, Math.ceil(apartments.length / PAGE_SIZE));
  const pagedApartments = apartments.slice(
    (aptPage - 1) * PAGE_SIZE,
    aptPage * PAGE_SIZE
  );
  // What happened in the owner's apartments: leases signed, deposits and rent
  // arriving, keys, protocols, move-outs. Sourced from the business facts
  // themselves, so an event that happened renders here whether or not the
  // owner-lifecycle mail for it ever went out — the Email history card on
  // Overview answers that second question separately.
  const { data: tenancyActivity } = useOwnerTenancyActivity(
    lessor?.id ?? null,
    apartments.map((a) => a.id),
    // unassignedUnits carry no apartment id (there's no apartment record) —
    // their property id is what scopes their bookings instead.
    unassignedUnits.map((u) => u.property_id)
  );

  // Every dated event on this owner, newest first — the logged actions (each
  // one its own entry, repeats included), the state-derived billing history
  // that predates the log, and the tenancy events above.
  const activity = useMemo(
    () =>
      [
        ...buildOwnerActivity({
          invoices,
          documents: data?.ownerDocs ?? [],
          submission: data?.submission ?? null,
          subscriptionStartedAt: data?.subscription?.started_at ?? null,
          ownerCreatedAt: current?.created_at ?? null,
          auditLogs,
          notes: noteEvents,
        }),
        ...(tenancyActivity ?? []),
      ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()),
    [
      invoices,
      data,
      current?.created_at,
      auditLogs,
      noteEvents,
      tenancyActivity,
    ]
  );

  const thisYear = new Date().getFullYear();
  const yearInvoices = invoices.filter((i) => {
    const d = new Date(`${String(i.period_month).slice(0, 10)}T12:00:00`);
    return !Number.isNaN(d.getTime()) && d.getFullYear() === thisYear;
  });
  const yearPaidCount = yearInvoices.filter((i) => i.status === "paid").length;
  const nextInvoiceDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth() + 1,
    1
  );

  // Schedule / history split — the same rule the tenant rent tab uses: the
  // schedule is what is still owed (this month and up to two months ahead), the
  // history is what has been settled. See splitOwnerInvoices for the guarantee
  // that no invoice falls between the two.
  const {
    schedule: realScheduleInvoices,
    history: paidInvoices,
    laterOpen,
    nextOpen: nextRealOpenInvoice,
  } = useMemo(() => splitOwnerInvoices(invoices), [invoices]);
  // The units the generator bills as separate invoices: every apartment, plus
  // one property-level unit per property holding rooms assigned to no
  // apartment. The same list `heldProperties` above rolls up — kept flat here,
  // because since 20260904120000 the invoice is issued at THIS granularity.
  const billableUnits = useMemo<OwnerBillableUnit[]>(
    () =>
      [...apartments, ...unassignedUnits].map((a) => ({
        apartmentId: a.id,
        propertyId: a.property_id,
        // Labels repeat across buildings ("Whg. 1" exists in most of them), so
        // the property qualifies every row. Matches the description the
        // generator writes onto the real invoice.
        title: a.unassigned
          ? (a.property_title ?? `Property ${a.property_id}`)
          : `${[a.label, a.floor].filter(Boolean).join(" · ")} – ${
              a.property_title ?? `Property ${a.property_id}`
            }`,
        name: a.unassigned
          ? (a.property_title ?? `Property ${a.property_id}`)
          : [a.label, a.floor].filter(Boolean).join(" · "),
        propertyTitle: a.property_title,
        collectionMode: a.collection_mode,
        config: {
          revenue_model: a.revenue_model,
          coliviq_fee_pct: a.coliviq_fee_pct,
          coliviq_fee_amount: a.coliviq_fee_amount,
          master_rent: a.master_rent,
        },
        // The GENERATOR's occupancy reading, not the booking-derived one, so a
        // forecast can't promise a fee the generator won't bill.
        occupiedRooms: a.generator_occupied_rooms,
        occupiedRent: a.generator_occupied_rent,
        degraded: a.rooms_degraded,
      })),
    [apartments, unassignedUnits]
  );
  // Fill in any property × month (this one + up to two ahead) with no real
  // invoice yet — see projectOwnerPropertyInvoices for why the schedule can't
  // rely on real rows alone, why a forecast is shaped like the one document the
  // property will be billed, and why the amounts are computed rather than
  // carried forward.
  const projectedInvoices = useMemo(
    () => projectOwnerPropertyInvoices(invoices, billableUnits),
    [invoices, billableUnits]
  );
  const scheduleInvoices = useMemo(
    () =>
      [...realScheduleInvoices, ...projectedInvoices].sort(
        (a, b) =>
          String(a.period_month).localeCompare(String(b.period_month)) ||
          // Within a month the rows are now several apartments, not one
          // portfolio total — order them by name so the same apartment sits in
          // the same place every month.
          invoiceUnitTitle(a, t).localeCompare(invoiceUnitTitle(b, t))
      ),
    [realScheduleInvoices, projectedInvoices, t]
  );
  // A still-unpaid real invoice always wins ("next" must never read as
  // resolved while money is actually owed); only once nothing real is open
  // does the next projected month stand in.
  const nextOpenInvoice = nextRealOpenInvoice ?? projectedInvoices[0] ?? null;
  const laterOpenCount = laterOpen.length;
  const openCount = scheduleInvoices.length + laterOpenCount;
  // Month-over-month change in what this owner was invoiced, same chip as the
  // analytics hero. Hidden when neither month has an invoice.
  const feeTrend = useMemo(() => invoicedMonthTrend(invoices), [invoices]);
  // Real money only — a projected month is a forecast, not yet owed.
  //
  // And OFFSET invoices are excluded: their fee was deducted from the rent
  // CoLiviq collected, so counting them here would report an owner as owing
  // money nobody will ever transfer — the same reading that drives "Open now",
  // the summary line, and how urgent this card looks. They are reported on
  // their own tile instead, so the fee stays visible without becoming a debt.
  const scheduleOpenCents = realScheduleInvoices
    .filter((i) => !isOffsetInvoice(i))
    .reduce((s, i) => s + (i.amount_cents || 0), 0);
  const scheduleOffsetCents = realScheduleInvoices
    .filter((i) => isOffsetInvoice(i))
    .reduce((s, i) => s + (i.amount_cents || 0), 0);
  const paidCents = paidInvoices.reduce((s, i) => s + (i.amount_cents || 0), 0);
  // The schedule's Net payout is the live payout the Owners payouts view shows
  // — this month's, and the payouts carried over from earlier months (COL-515),
  // so an invoice the band lists never reads "—" here. The groups ride on the
  // Owners list row (useOwnersList); an owner opened from another screen, like
  // the apartment popup, arrives as a plain lessor with none, and its schedule
  // then reads "—" instead of a guess.
  const ownerListRow = lessor as Partial<OwnerListRow> | null;
  const payoutGroups = ownerListRow?.payoutGroups;
  const carriedOverPayoutGroups = ownerListRow?.carriedOverPayoutGroups;
  const liveNetPayout = useMemo(
    () =>
      liveNetPayoutCentsByInvoiceId(
        realScheduleInvoices,
        payoutGroups && [...payoutGroups, ...(carriedOverPayoutGroups ?? [])]
      ),
    [realScheduleInvoices, payoutGroups, carriedOverPayoutGroups]
  );
  // What "Record payout" will save, per schedule row (COL-516): narrower than
  // the column — see payoutToRecordCentsByInvoiceId. The carried-over groups
  // are offered alongside this month's: the action records the payout of
  // whichever invoice it is triggered on, and an earlier month's remainder is
  // an invoice like any other (COL-515's band is where it is settled from).
  const payoutToRecord = useMemo(
    () =>
      payoutToRecordCentsByInvoiceId(
        invoices,
        payoutGroups && [...payoutGroups, ...(carriedOverPayoutGroups ?? [])]
      ),
    [invoices, payoutGroups, carriedOverPayoutGroups]
  );

  // Marking an invoice paid moves it from the schedule to the history, so a
  // page can vanish under the pager — clamp instead of rendering an empty page.
  // Paged by APARTMENT, not by row: each apartment renders as its own table,
  // and paging mid-apartment would split one flat's months across two pages.
  const scheduleGroups = useMemo(
    () =>
      groupOwnerInvoicesByProperty(
        scheduleInvoices,
        (inv) => describeInvoiceProperty(inv, t),
        invoiceRowState
      ),
    [scheduleInvoices, t]
  );
  const schedTotalPages = Math.max(
    1,
    Math.ceil(scheduleGroups.length / SCHEDULE_GROUPS_PER_PAGE)
  );
  const schedPageSafe = Math.min(schedPage, schedTotalPages);
  const pagedSchedule = scheduleGroups.slice(
    (schedPageSafe - 1) * SCHEDULE_GROUPS_PER_PAGE,
    schedPageSafe * SCHEDULE_GROUPS_PER_PAGE
  );
  const historyGroups = useMemo(
    () =>
      groupOwnerInvoicesByProperty(
        paidInvoices,
        (inv) => describeInvoiceProperty(inv, t),
        invoiceRowState
      ),
    [paidInvoices, t]
  );
  const histTotalPages = Math.max(
    1,
    Math.ceil(historyGroups.length / SCHEDULE_GROUPS_PER_PAGE)
  );
  const histPageSafe = Math.min(invPage, histTotalPages);
  const pagedHistory = historyGroups.slice(
    (histPageSafe - 1) * SCHEDULE_GROUPS_PER_PAGE,
    histPageSafe * SCHEDULE_GROUPS_PER_PAGE
  );

  function startEditing() {
    setTab("overview");
    setIsEditing(true);
  }

  const submission = data?.submission ?? null;
  // Every submission on file, newest first — the Onboarding tab nests them all.
  const submissions = data?.submissions ?? [];
  const notesRequired = docType === "other";

  function setField<K extends keyof Lessor>(key: K, value: Lessor[K]) {
    setCurrent((c) => (c ? { ...c, [key]: value } : c));
  }

  /**
   * The payload for a save. Built from the KNOWN lessors columns only:
   * `current` is seeded from the list row, an enriched OwnerListRow carrying
   * derived fields (currentInvoiceStatus, propertiesCount, onboardingStage,
   * propertyList) that aren't columns on `lessors` — spreading them made the
   * update fail with "Could not find the 'currentInvoiceStatus' column".
   */
  function editPayload(from: Lessor): LessorInput {
    return {
      name: from.name,
      type: from.type,
      address: from.address,
      city_postal: from.city_postal,
      email: from.email,
      phone: from.phone,
      rep_name: from.rep_name,
      rep_role: from.rep_role,
      rep_note: from.rep_note,
      bank_holder: from.bank_holder,
      bank_iban: from.bank_iban,
      bank_bic: from.bank_bic,
      bank_name: from.bank_name,
      is_platform: from.is_platform,
    };
  }

  function saveEdit(input: LessorInput, reason?: string) {
    if (!current) return;
    updateMut.mutate(
      { id: current.id, input, reason },
      {
        onSuccess: (saved) => {
          // Adopt the row the database returned rather than leaving `current`
          // holding what was typed: anything the write normalised or defaulted
          // server-side shows immediately, and the modal is displaying
          // confirmed data from the moment the save lands — not an optimistic
          // guess waiting on the background refetch to agree with it.
          setCurrent((c) => (c ? { ...c, ...saved } : saved));
          toast.success(
            t("admin.ownerDetail.toastOwnerUpdated", "Owner updated")
          );
          setIsEditing(false);
          setBankReason(null);
        },
        onError: (e) =>
          // Supabase/PostgREST errors are plain objects, not Error instances —
          // an `instanceof Error` check drops their .message and hides the real
          // cause (e.g. a 404 RPC or a missing column on a drifted DB). Pull the
          // message off whatever shape we got so the admin sees the actual error.
          toast.error(
            formatSupabaseError(e) ||
              t("admin.ownerDetail.toastSaveFailed", "Could not save changes")
          ),
      }
    );
  }

  function handleSaveEdit() {
    if (!current) return;
    const input = editPayload(current);
    // Removing payout details is refused by the database without a reason, and
    // the reason is recorded beside the removal on the Activity tab. Ask for it
    // here rather than letting the admin discover the refusal as a raw error
    // after they have already typed the rest of their edit.
    if (removesBankDetails(lessor, input)) {
      setBankReason({ input, text: "" });
      return;
    }
    saveEdit(input);
  }

  function handleCancelEdit() {
    setCurrent(lessor);
    setIsEditing(false);
  }

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      // "Other" documents must carry a note explaining what they are.
      if (docType === "other" && !notes.trim()) {
        toast.error(
          t(
            "admin.ownerDetail.docs.noteRequired",
            "Add a note before uploading an “Other” document."
          )
        );
        return;
      }
      const noteToSave = notes;
      Array.from(files).forEach((file) => {
        upload.mutate(
          {
            file,
            docType,
            notes: noteToSave,
            ownerUserId: submission?.supabase_user_id ?? null,
          },
          {
            onSuccess: () => toast.success(`Uploaded ${file.name}`),
            onError: (e) =>
              toast.error(
                e instanceof Error ? e.message : `Could not upload ${file.name}`
              ),
          }
        );
      });
      setNotes("");
    },
    [upload, docType, notes, submission, t]
  );

  async function handleResendLogin() {
    if (!lessor?.email) return;
    setResending(true);
    try {
      // Omit redirect_to so the edge function computes its owner-aware
      // destination: pending-onboarding owners get deep-linked back into
      // onboarding (via their token), verified portal users go to
      // /admin?tab=analytics. Passing a hardcoded redirect defeats that.
      const { error } = await supabase.functions.invoke(
        "admin-send-login-link",
        {
          body: {
            email: lessor.email,
          },
        }
      );
      if (error) throw error;
      await logOwnerAudit({
        lessorId: lessor?.id,
        action: "owner_login_link_sent",
        resourceType: "lessor",
        resourceId: String(lessor?.id ?? ""),
        metadata: { recipient: lessor.email },
      });
      queryClient.invalidateQueries({
        queryKey: OWNER_AUDIT_KEY(lessor?.id ?? null),
      });
      toast.success(
        t("admin.ownerDetail.toastLoginLinkSent", "Login link sent")
      );
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : t(
              "admin.ownerDetail.toastLoginLinkFailed",
              "Could not send login link"
            )
      );
    } finally {
      setResending(false);
    }
  }

  function handleDeleteDoc(doc: OwnerDoc) {
    // Guard against a single misclick permanently destroying a document
    // (proof of ownership, signed correspondence) from inside the modal.
    if (
      !window.confirm(
        `Delete “${doc.title}”? This permanently removes the document and cannot be undone.`
      )
    )
      return;
    remove.mutate(doc, {
      onSuccess: () =>
        toast.success(
          t("admin.ownerDetail.toastDocumentRemoved", "Document removed")
        ),
      onError: (e) =>
        toast.error(
          e instanceof Error
            ? e.message
            : t(
                "admin.ownerDetail.toastDocumentRemoveFailed",
                "Could not remove document"
              )
        ),
    });
  }

  // Overview reads the latest submission; each nested Onboarding block reads its
  // own, since a re-onboarding may have chosen a different method.
  const feePayment = paymentMethodLabel(submission?.payment_method, t);

  return (
    <>
      <Dialog
        open={!!lessor || !!accountRow}
        onOpenChange={(o) => !o && onClose()}
      >
        {/* `flex flex-col` overrides DialogContent's default `grid`: with a fixed
            height (expanded) the grid stretched its rows, which padded a tall
            blank band between the header and the tabs. As a flex column the
            header and tab bar keep their natural height and the panel below
            takes the remaining space. */}
        <DialogContent
          className={`flex flex-col gap-0 overflow-hidden p-0 transition-[max-width,width,height] duration-200 ${
            expanded ? "h-[96vh] w-[98vw] max-w-[98vw]" : "max-w-5xl"
          }`}
        >
          {/* Header */}
          {/* With header actions there are five controls beside the avatar:
              below tablet width that wrapped the name and email (one word per
              line on a phone) and pushed Edit off the dialog's right edge.
              Give the controls their own row above, right-aligned, so the
              identity keeps full width. Only set when actions are passed
              (COL-475, as COL-471 did). */}
          <DialogHeader
            className={cn(
              "flex shrink-0 flex-row items-start justify-between gap-4 space-y-0 border-b p-6",
              headerActions && "flex-wrap md:flex-nowrap"
            )}
          >
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-teal-700 text-sm font-semibold text-white">
                {initialsFromName(shown?.name)}
              </span>
              <div>
                {/* The name links into the owner's portfolio: straight to the
                    apartment page when they hold exactly one, otherwise to the
                    Apartments tab, which lists every one of them. */}
                <DialogTitle className="text-xl">
                  <OwnerApartmentLink
                    lessorId={shown?.id}
                    name={shown?.name ?? "—"}
                    chevron
                    onNavigate={onClose}
                    className="group inline-flex items-center gap-1 underline-offset-4 hover:text-primary hover:underline"
                  />
                </DialogTitle>
                <p className="text-sm text-muted-foreground">
                  {[shown?.email, shown?.phone].filter(Boolean).join(" · ") ||
                    "—"}
                </p>
              </div>
            </div>
            <div
              className={cn(
                "flex items-center gap-2 pr-8",
                headerActions &&
                  "order-first w-full justify-end md:order-none md:w-auto"
              )}
            >
              {/* Hidden mid-edit, as the other tabs are disabled: its items
                  would open a second editor, delete the record, or close the
                  profile over the admin's unsaved typing. */}
              {!isEditing && headerActions}
              {hasBilling && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-8 w-8 text-muted-foreground"
                  onClick={() => setNotesOpen((v) => !v)}
                  aria-label={
                    openNoteCount > 0
                      ? `${t("admin.ownerDetail.sectionInternalNotes", "Internal notes")} — ${openNoteCount} ${t("admin.ownerDetail.notesOpenSuffix", "open")}`
                      : t(
                          "admin.ownerDetail.sectionInternalNotes",
                          "Internal notes"
                        )
                  }
                  title={t(
                    "admin.ownerDetail.sectionInternalNotes",
                    "Internal notes"
                  )}
                >
                  <ClipboardList className="h-4 w-4" />
                  {openNoteCount > 0 && (
                    <span className="absolute -right-1 -top-1 grid h-[18px] min-w-[18px] place-items-center rounded-full border-2 border-white bg-teal-800 px-1 text-[10px] font-extrabold leading-none text-white">
                      {openNoteCount > 99 ? "99+" : openNoteCount}
                    </span>
                  )}
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground"
                onClick={() => setExpanded((v) => !v)}
                aria-label={
                  expanded
                    ? t("admin.ownerDetail.collapseWindow", "Collapse window")
                    : t("admin.ownerDetail.expandWindow", "Expand window")
                }
                title={
                  expanded
                    ? t("admin.ownerDetail.collapse", "Collapse")
                    : t("admin.ownerDetail.expand", "Expand")
                }
              >
                {expanded ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </Button>
              {!isEditing && <StatusPill status={submission?.status} />}
              {/* Editing writes to the `lessors` row — with none, there is
                  nothing to save into, so the button is absent rather than
                  offered and then failing. The account itself is edited from
                  the ⋯ menu (the Owners row's, or this header's). */}
              {!hasBilling ? null : isEditing ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelEdit}
                    disabled={updateMut.isPending}
                  >
                    <X className="mr-1.5 h-3.5 w-3.5" />{" "}
                    {t("admin.ownerDetail.actionCancel", "Cancel")}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveEdit}
                    disabled={updateMut.isPending}
                  >
                    <Save className="mr-1.5 h-3.5 w-3.5" />
                    {updateMut.isPending
                      ? t("admin.roles.editUser.saving", "Saving…")
                      : t("admin.ownerDetail.save", "Save")}
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={startEditing}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />{" "}
                  {t("admin.ownerDetail.actionEdit", "Edit")}
                </Button>
              )}
            </div>
          </DialogHeader>

          <Tabs
            value={tab}
            onValueChange={setTab}
            className="flex min-h-0 flex-1 flex-col"
          >
            {/* Eight tabs, one of them long: the strip scrolls rather than
                wrapping onto a second line or squeezing the labels — in the
                collapsed (non-expanded) modal it no longer fits. */}
            <div className="shrink-0 overflow-x-auto border-b px-6">
              <TabsList className="h-auto w-full justify-start gap-1 whitespace-nowrap rounded-none bg-transparent p-0">
                {[
                  ["overview", t("admin.ownerDetail.tabOverview", "Overview")],
                  ["invoices", t("admin.ownerDetail.tabInvoices", "Invoices")],
                  [
                    "apartments",
                    t("admin.ownerDetail.tileApartments", "Apartments"),
                  ],
                  [
                    "documents",
                    t("admin.ownerDetail.tabDocuments", "Documents"),
                  ],
                  // Its own tab, never merged into Documents: these files are
                  // admin-only working material (a WhatsApp screenshot, a
                  // scribbled agreement), not part of the owner's file. Mixing
                  // the two makes it easy to hand an owner something that was
                  // never meant for them.
                  [
                    "internal-docs",
                    t(
                      "admin.ownerDetail.tabInternalDocs",
                      "Internal documents"
                    ),
                  ],
                  [
                    "onboarding",
                    t("admin.ownerDetail.tabOnboarding", "Onboarding"),
                  ],
                  [
                    "subscription",
                    t("admin.ownerDetail.tabSubscription", "Subscription"),
                  ],
                  ["activity", t("admin.ownerDetail.tabActivity", "Activity")],
                ].map(([v, label]) => (
                  <TabsTrigger
                    key={v}
                    value={v}
                    disabled={isEditing && v !== "overview"}
                    className="rounded-none border-b-2 border-transparent px-3 py-3 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {/* Expanded: fill whatever the header and tab bar leave (no
                `calc(96vh - 150px)` guess, which was the source of the dead
                space). Collapsed: the dialog is content-sized, so cap the
                scroller instead. */}
            <div
              className={`min-h-0 flex-1 overflow-y-auto p-6 ${
                expanded ? "" : "max-h-[65vh]"
              }`}
            >
              {/* ── Overview ── */}
              <TabsContent value="overview" className="mt-0 space-y-4">
                {!hasBilling && (
                  <NoBillingRecordNote
                    text="This owner can log in, but there is no billing entity to invoice — so no apartments, invoices, documents or onboarding are attached to them yet."
                    onAdd={onAddBillingEntity}
                  />
                )}
                {/* Portfolio KPI strip — at-a-glance size + earnings. Every
                    number here is read off the billing record, so with none
                    they are UNKNOWN, not zero: "0 apartments" would contradict
                    the properties this owner's account is assigned to. */}
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  <StatTile
                    label={t("admin.ownerDetail.tileApartments", "Apartments")}
                    value={hasBilling ? (data?.stats.apartmentCount ?? 0) : "—"}
                    hint={
                      !hasBilling
                        ? "Apartments attach to the billing record"
                        : data && data.stats.propertyCount > 0
                          ? `In ${data.stats.propertyCount} propert${
                              data.stats.propertyCount === 1 ? "y" : "ies"
                            }`
                          : undefined
                    }
                  />
                  <StatTile
                    label={t("admin.ownerDetail.tileRooms", "Rooms")}
                    value={hasBilling ? (data?.stats.roomCount ?? 0) : "—"}
                  />
                  <StatTile
                    label={t(
                      "admin.ownerDetail.tileMonthlyRevenue",
                      "Monthly revenue"
                    )}
                    value={
                      !hasBilling || data?.stats.revenueDegraded
                        ? "—"
                        : formatEUR(data?.stats.totalMonthlyFees ?? 0)
                    }
                    hint={
                      !hasBilling
                        ? t(
                            "admin.ownerDetail.tileNoBillingRecord",
                            "Nothing is billed without a billing record"
                          )
                        : data?.stats.revenueDegraded
                          ? t(
                              "admin.ownerDetail.tileOccupancyUnavailable",
                              "Occupancy data unavailable right now — not a verified €0"
                            )
                          : // Operators see their own company name here, so the
                            // fee copy stays out of i18n for them; CoLiviq staff
                            // keep the translatable string.
                            isOperator
                            ? `${platformName}'s fee across occupied rooms`
                            : t(
                                "admin.ownerDetail.tileFeeExplainer",
                                "coliviq's fee across occupied rooms"
                              )
                    }
                  />
                  <StatTile
                    label={t(
                      "admin.ownerDetail.tileFeesPaidYtd",
                      "Fees paid (YTD)"
                    )}
                    value={
                      hasBilling
                        ? formatEUR(data?.stats.feesPaidThisYear ?? 0)
                        : "—"
                    }
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <SectionCard
                    title={t("admin.ownerDetail.sectionContact", "Contact")}
                  >
                    <EditRow
                      label={t("admin.ownerDetail.fieldName", "Name")}
                      editing={isEditing}
                      value={shown?.name}
                      onChange={(v) => setField("name", v)}
                    />
                    <EditRow
                      label={t("admin.ownerDetail.fieldEmail", "Email")}
                      editing={isEditing}
                      value={shown?.email}
                      onChange={(v) => setField("email", v)}
                    />
                    <EditRow
                      label={t("admin.ownerDetail.fieldPhone", "Phone")}
                      editing={isEditing}
                      value={shown?.phone}
                      onChange={(v) => setField("phone", v)}
                    />
                    {isEditing ? (
                      <>
                        <EditRow
                          label={t("admin.ownerDetail.fieldAddress", "Address")}
                          editing
                          value={shown?.address}
                          onChange={(v) => setField("address", v)}
                        />
                        <EditRow
                          label={t(
                            "admin.ownerDetail.fieldPostalCity",
                            "Postal / city"
                          )}
                          editing
                          value={shown?.city_postal}
                          onChange={(v) => setField("city_postal", v)}
                        />
                      </>
                    ) : (
                      <Row
                        label="Address"
                        value={
                          [shown?.address, shown?.city_postal]
                            .filter(Boolean)
                            .join(", ") || "—"
                        }
                      />
                    )}
                    <Row
                      label={
                        hasBilling
                          ? t(
                              "admin.ownerDetail.fieldOwnerSince",
                              "Owner since"
                            )
                          : t(
                              "admin.ownerDetail.fieldAccountCreated",
                              "Account created"
                            )
                      }
                      value={formatDate(shown?.created_at) ?? "—"}
                    />
                    {/* The portal account, and whether it still agrees with
                        this record. An owner is stored twice — the account they
                        log in with, and this record their invoices are
                        addressed from — and the two used to drift in silence.
                        They sync now, but records that drifted BEFORE that were
                        deliberately left alone rather than reconciled by guess,
                        so the disagreement is shown here for a human to
                        settle. */}
                    <Row
                      label={t(
                        "admin.ownerDetail.fieldPortalAccount",
                        "Portal account"
                      )}
                      value={
                        account ? (
                          accountNameDrift ? (
                            <span className="text-amber-700 dark:text-amber-500">
                              Signs in as “{accountNameDrift}” — differs from
                              the name above
                            </span>
                          ) : (
                            (account.email ??
                            t("admin.ownerDetail.portalLinked", "Linked"))
                          )
                        ) : (
                          <span className="text-muted-foreground">
                            {t(
                              "admin.ownerDetail.noPortalAccount",
                              "No portal account"
                            )}
                          </span>
                        )
                      }
                    />
                  </SectionCard>

                  <SectionCard
                    title={t(
                      "admin.ownerDetail.sectionBankPayment",
                      "Bank & payment"
                    )}
                  >
                    <EditRow
                      label={t("admin.ownerDetail.fieldBank", "Bank")}
                      editing={isEditing}
                      value={current?.bank_name}
                      onChange={(v) => setField("bank_name", v)}
                    />
                    <EditRow
                      label="IBAN"
                      editing={isEditing}
                      value={current?.bank_iban}
                      onChange={(v) => setField("bank_iban", v)}
                      mono
                      display={
                        <span className="font-mono">
                          {maskIban(current?.bank_iban)}
                        </span>
                      }
                    />
                    <EditRow
                      label="BIC"
                      editing={isEditing}
                      value={current?.bank_bic}
                      onChange={(v) => setField("bank_bic", v)}
                      mono
                    />
                    <Row
                      label={t(
                        "admin.ownerDetail.fieldFeePayment",
                        "Fee payment"
                      )}
                      value={hasBilling ? feePayment : "—"}
                    />
                  </SectionCard>

                  {/* Representor — what prints after "vertreten durch" on this
                      owner's contracts, and the default every one of their
                      apartments inherits. Third in the two-column grid, so it
                      sits directly under Contact. Already part of the save
                      payload above; it just had no surface here until now. */}
                  <SectionCard
                    title={t(
                      "admin.ownerDetail.fieldRepresentor",
                      "Representor"
                    )}
                  >
                    <EditRow
                      label="Representor"
                      editing={isEditing}
                      value={current?.rep_name}
                      onChange={(v) => setField("rep_name", v)}
                    />
                    <EditRow
                      label={t("admin.ownerDetail.fieldNote", "Note")}
                      editing={isEditing}
                      value={current?.rep_note}
                      onChange={(v) => setField("rep_note", v)}
                    />
                    <p className="pt-1 text-xs text-muted-foreground">
                      Default for all of this owner&apos;s apartments. A single
                      apartment can override it in its own settings.
                    </p>
                  </SectionCard>

                  <OwnerEmailHistoryCard
                    lessorId={lessor?.id ?? null}
                    apartmentIds={(data?.apartments ?? []).map((a) => a.id)}
                  />
                </div>

                {/* Recent invoices — the latest few without leaving Overview;
                    the Invoices tab holds the full paginated history. */}
                <SectionCard
                  title={t(
                    "admin.ownerDetail.sectionRecentInvoices",
                    "Recent invoices"
                  )}
                >
                  {isLoading || invoicesLoading ? (
                    <p className="text-sm text-muted-foreground">
                      {t("common.loading", "Loading…")}
                    </p>
                  ) : invoices.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "admin.ownerDetail.noManagementInvoices",
                        "No management invoices yet."
                      )}
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {invoices.slice(0, 4).map((inv) => (
                        <div
                          key={inv.id}
                          className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2 text-sm"
                        >
                          <span className="w-28 shrink-0 font-medium">
                            {formatDate(inv.period_month)?.replace(
                              /^\d+ /,
                              ""
                            ) ?? inv.period_month}
                          </span>
                          <span className="hidden font-mono text-xs text-muted-foreground sm:inline">
                            {inv.invoice_no ?? "—"}
                          </span>
                          <RemainderInvoiceTag row={inv} className="truncate" />
                          <span className="ml-auto font-medium tabular-nums">
                            {formatCents(inv.amount_cents, inv.currency)}
                          </span>
                          <InvoiceStatusPill
                            status={inv.displayStatus ?? inv.status}
                            settlement={inv.settlement}
                          />
                        </div>
                      ))}
                      {invoices.length > 4 && (
                        <button
                          type="button"
                          onClick={() => setTab("invoices")}
                          className="mt-1 text-xs font-medium text-primary hover:underline"
                        >
                          View all {invoices.length} invoices →
                        </button>
                      )}
                    </div>
                  )}
                </SectionCard>
              </TabsContent>

              {/* ── Invoices ── */}
              <TabsContent value="invoices" className="mt-0">
                {!hasBilling ? (
                  <NoBillingRecordNote
                    text="Management invoices are made out to the billing record. Nothing can be invoiced until this owner has one."
                    onAdd={onAddBillingEntity}
                  />
                ) : isLoading || invoicesLoading ? (
                  <RowSkeleton rows={4} />
                ) : invoices.length === 0 ? (
                  <EmptyBlock
                    icon={FileText}
                    text="No management invoices yet."
                  />
                ) : (
                  // The modal is capped at max-w-5xl, so a 260px side column
                  // leaves ~700px — not enough for the invoice row's actions
                  // (measured 809px), which clipped t("admin.ownerDetail.actionMarkPaid", "Mark paid"). Stack the fee
                  // summary above the table unless the window is expanded,
                  // where there is real room for both.
                  <div
                    className={cn(
                      "grid gap-4",
                      expanded ? "xl:grid-cols-[260px_1fr]" : "grid-cols-1"
                    )}
                  >
                    {/* Management fee summary. Four metrics: at full width they
                        sit side by side as tiles; in the narrow side column
                        (expanded window) they stack. */}
                    <div className="rounded-2xl border bg-card p-5">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold">
                            {t(
                              "admin.ownerDetail.fieldManagementFee",
                              "Management fee"
                            )}
                          </p>
                          {data?.stats.revenueModelLabel &&
                            data.stats.revenueModelLabel !== "—" && (
                              <p className="mt-0.5 text-xs text-muted-foreground">
                                {data.stats.revenueModelLabel}
                              </p>
                            )}
                        </div>
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
                          <FileText className="h-4 w-4" />
                        </span>
                      </div>

                      {/* 2×2 rather than 4-across: the modal caps at ~1024px,
                          where four tiles would crush the trend chip. */}
                      <div
                        className={cn(
                          "mt-4 grid gap-3",
                          expanded ? "grid-cols-1" : "sm:grid-cols-2"
                        )}
                      >
                        <MetricTile
                          label={t(
                            "admin.ownerDetail.fieldMonthlyFee",
                            "Monthly fee"
                          )}
                        >
                          <p className="text-2xl font-semibold tracking-tight tabular-nums">
                            {data?.stats.currentMonthlyFee != null
                              ? formatEUR(data.stats.currentMonthlyFee)
                              : "—"}
                          </p>
                        </MetricTile>

                        {/* Invoiced this month vs last — the same arrow chip the
                            analytics hero uses for period-over-period. */}
                        <MetricTile
                          label={`${t("admin.ownerDetail.invoicedPrefix", "Invoiced")} — ${currentMonthLabel(new Date(), language)}`}
                        >
                          {feeTrend ? (
                            <>
                              <p className="text-2xl font-semibold tracking-tight tabular-nums">
                                {formatCents(feeTrend.current, "EUR")}
                              </p>
                              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span
                                  className={cn(
                                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1",
                                    feeTrend.direction === "up"
                                      ? "bg-emerald-50 text-emerald-700 ring-emerald-100"
                                      : feeTrend.direction === "down"
                                        ? "bg-rose-50 text-rose-700 ring-rose-100"
                                        : "bg-muted text-muted-foreground ring-border"
                                  )}
                                >
                                  {feeTrend.direction === "up" ? (
                                    <ArrowUpRight className="h-3.5 w-3.5" />
                                  ) : feeTrend.direction === "down" ? (
                                    <ArrowDownRight className="h-3.5 w-3.5" />
                                  ) : null}
                                  {/* An unchanged month reads as t("admin.ownerDetail.noChange", "No change"),
                                      not "+0.0%". */}
                                  {feeTrend.direction === "flat"
                                    ? "No change"
                                    : feeTrend.pct != null
                                      ? `${feeTrend.pct > 0 ? "+" : ""}${feeTrend.pct.toFixed(1)}%`
                                      : "New"}
                                  <span className="font-normal opacity-70">
                                    vs {feeTrend.label}
                                  </span>
                                </span>
                                {feeTrend.delta !== 0 && (
                                  <span className="text-xs tabular-nums text-muted-foreground">
                                    {feeTrend.label}:{" "}
                                    {formatCents(feeTrend.prevTotal, "EUR")}
                                  </span>
                                )}
                              </div>
                            </>
                          ) : (
                            <p className="text-2xl font-semibold tracking-tight text-muted-foreground">
                              —
                            </p>
                          )}
                        </MetricTile>

                        <MetricTile label={`Months paid — ${thisYear}`}>
                          <p className="text-2xl font-semibold tracking-tight tabular-nums">
                            {yearPaidCount}
                            <span className="text-base font-medium text-muted-foreground">
                              {" "}
                              / {yearInvoices.length}
                            </span>
                          </p>
                          <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-emerald-500 transition-[width] duration-500"
                              style={{
                                width: `${
                                  yearInvoices.length > 0
                                    ? (yearPaidCount / yearInvoices.length) *
                                      100
                                    : 0
                                }%`,
                              }}
                            />
                          </div>
                        </MetricTile>

                        <MetricTile
                          label={t(
                            "admin.ownerDetail.fieldNextInvoice",
                            "Next invoice"
                          )}
                        >
                          {nextOpenInvoice ? (
                            // The next OPEN invoice — a still-unpaid August fee
                            // is what's next, not the September one yet to be
                            // issued.
                            <>
                              <p className="text-base font-semibold tracking-tight">
                                {monthLabel(
                                  nextOpenInvoice.period_month,
                                  language
                                )}{" "}
                                {t("admin.ownerDetail.feeSuffix", "fee")}
                              </p>
                              <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3 shrink-0" />
                                {nextOpenInvoice.__projected
                                  ? t(
                                      "admin.ownerDetail.invoiceNotIssued",
                                      "Not yet issued"
                                    )
                                  : // An offset invoice's due_date is always
                                    // null — that is not "open with no due
                                    // date set" (a transfer invoice the owner
                                    // owes with nothing to chase it by), it is
                                    // settled from rent already collected and
                                    // simply awaiting the admin's confirmation.
                                    isOffsetInvoice(nextOpenInvoice)
                                    ? t(
                                        "admin.ownerDetail.invoiceOffsetPending",
                                        "Offset against rent — awaiting confirmation"
                                      )
                                    : nextOpenInvoice.due_date
                                      ? `${
                                          (nextOpenInvoice.displayStatus ??
                                            nextOpenInvoice.status) ===
                                          "overdue"
                                            ? t(
                                                "admin.ownerDetail.invoiceOverdueSince",
                                                "Overdue since"
                                              )
                                            : t(
                                                "admin.ownerDetail.invoiceDueOn",
                                                "Due on"
                                              )
                                        } ${formatDate(nextOpenInvoice.due_date) ?? ""}`
                                      : t(
                                          "admin.ownerDetail.invoiceOpenNoDue",
                                          "Open — no due date set"
                                        )}
                              </p>
                              {openCount > 1 && (
                                <p className="mt-1 text-xs text-muted-foreground">
                                  {`+${openCount - 1} `}
                                  {openCount > 2
                                    ? t(
                                        "admin.ownerDetail.moreUpcomingInvoicesPlural",
                                        "more upcoming invoices"
                                      )
                                    : t(
                                        "admin.ownerDetail.moreUpcomingInvoiceSingular",
                                        "more upcoming invoice"
                                      )}
                                </p>
                              )}
                            </>
                          ) : (
                            // Nothing open — project the next monthly fee run.
                            <>
                              <p className="text-base font-semibold tracking-tight">
                                {monthLabel(
                                  nextInvoiceDate.toISOString(),
                                  language
                                )}{" "}
                                {t("admin.ownerDetail.feeSuffix", "fee")}
                              </p>
                              <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3 shrink-0" />
                                {t(
                                  "admin.ownerDetail.issuesOnPrefix",
                                  "Issues on"
                                )}{" "}
                                {nextInvoiceDate.toLocaleDateString(
                                  language === "de" ? "de-DE" : "en-GB",
                                  {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  }
                                )}
                              </p>
                            </>
                          )}
                        </MetricTile>
                      </div>
                    </div>

                    <div className="min-w-0 space-y-3">
                      {/* Invoice schedule — what's still owed, soonest first. */}
                      <CollapsibleTableCard
                        icon={Clock}
                        accent="bg-amber-50 text-amber-700 ring-amber-100"
                        title={t(
                          "admin.ownerDetail.sectionInvoiceSchedule",
                          "Invoice schedule"
                        )}
                        // Counts the rows this card actually lists, so the pill
                        // and the money below it describe the same set. Invoices
                        // dated beyond the window are reported by the footnote.
                        count={scheduleInvoices.length}
                        summary={
                          scheduleInvoices.length > 0
                            ? realScheduleInvoices.length > 0
                              ? [
                                  `${formatCents(scheduleOpenCents, "EUR")} open`,
                                  // Named separately, never folded into "open":
                                  // an offset fee is settled from rent, not
                                  // owed, so adding the two would overstate
                                  // what this owner has to pay.
                                  scheduleOffsetCents > 0
                                    ? `${formatCents(scheduleOffsetCents, "EUR")} offset against rent`
                                    : null,
                                  "this month and up to two ahead",
                                ]
                                  .filter(Boolean)
                                  .join(" · ")
                              : "Not yet issued · this month and up to two ahead"
                            : openCount === 0
                              ? t(
                                  "admin.ownerDetail.invoiceNothingOpen",
                                  "Nothing open"
                                )
                              : `Nothing open in this window · ${laterOpenCount} dated later`
                        }
                        stats={[
                          {
                            label: t(
                              "admin.ownerDetail.invoiceOpenNow",
                              "Open now"
                            ),
                            value: formatCents(scheduleOpenCents, "EUR"),
                          },
                          // Only shown once there is offset billing to report,
                          // so an owner_collects-only owner keeps the same two
                          // tiles they have today.
                          ...(scheduleOffsetCents > 0
                            ? [
                                {
                                  label: t(
                                    "admin.ownerDetail.invoiceOffsetNow",
                                    "Offset against rent"
                                  ),
                                  value: formatCents(
                                    scheduleOffsetCents,
                                    "EUR"
                                  ),
                                },
                              ]
                            : []),
                          {
                            label: "Not yet issued",
                            value: projectedInvoices.length,
                          },
                        ]}
                        help={
                          <>
                            <HelpStep
                              n={1}
                              title={t(
                                "admin.ownerDetail.scheduleHint",
                                "This month, plus two ahead"
                              )}
                            >
                              The schedule always shows what's still owed for
                              the current month and up to two months out.
                            </HelpStep>
                            <HelpStep
                              n={2}
                              title={t(
                                "admin.ownerDetail.scheduleNotBilled",
                                "Scheduled isn't billed yet"
                              )}
                            >
                              A row marked "Scheduled" is a forecast, not a real
                              invoice — nothing has been sent or is owed yet. It
                              becomes real automatically once the monthly
                              billing run reaches that month.
                            </HelpStep>
                            <HelpStep
                              n={3}
                              title={t(
                                "admin.ownerDetail.scheduleSooner",
                                "Need it sooner?"
                              )}
                            >
                              Use Create on a scheduled row to write the real
                              invoice now, at the amount you confirm.
                            </HelpStep>
                            <HelpStep
                              n={4}
                              title={t(
                                "admin.ownerDetail.scheduleLocked",
                                "Locked once it's sent"
                              )}
                            >
                              An issued invoice's amount and due date can't be
                              edited directly — that's a legal requirement, not
                              a limitation. Use Cancel &amp; reissue instead:
                              the original is voided and kept on file, and a
                              replacement with a new invoice number carries the
                              correct figures. Send the new one to the owner.
                            </HelpStep>
                          </>
                        }
                        open={schedOpen}
                        onToggle={() => setSchedOpen((v) => !v)}
                      >
                        {scheduleInvoices.length === 0 ? (
                          <p className="rounded-xl border border-dashed bg-card px-3 py-6 text-center text-sm text-muted-foreground">
                            {/* t("admin.ownerDetail.invoiceAllPaid", "Everything is paid") is only true when NOTHING is
                                open — the footnote below can otherwise report
                                open invoices right under this line. */}
                            {openCount === 0
                              ? t(
                                  "admin.ownerDetail.invoiceNoneOpenAllPaid",
                                  "No open invoices — everything issued is paid."
                                )
                              : t(
                                  "admin.ownerDetail.invoiceNoneOpenWindow",
                                  "Nothing open in this window."
                                )}
                          </p>
                        ) : (
                          <>
                            <OwnerInvoiceTable
                              groups={pagedSchedule}
                              renderActions={renderActions}
                              netPayoutCents={(inv) =>
                                liveNetPayout.get(inv.id) ?? null
                              }
                              recordPayoutCents={(inv) =>
                                payoutToRecord.get(inv.id) ?? null
                              }
                              heldProperties={heldProperties}
                              defaultOpen
                            />
                            <Pager
                              page={schedPageSafe}
                              totalPages={schedTotalPages}
                              onPage={setSchedPage}
                              total={scheduleGroups.length}
                              pageSize={SCHEDULE_GROUPS_PER_PAGE}
                            />
                          </>
                        )}
                        {/* Never silently swallow an unpaid invoice that sits
                            beyond the two-month window. */}
                        {laterOpenCount > 0 && (
                          <p className="mt-2 text-xs text-muted-foreground">
                            {laterOpenCount} further open invoice
                            {laterOpenCount > 1 ? "s" : ""} dated beyond{" "}
                            {monthLabel(invoiceScheduleWindowEnd(), language)} —
                            not shown here.
                          </p>
                        )}
                      </CollapsibleTableCard>

                      {/* Invoice history — settled invoices, newest first. */}
                      <CollapsibleTableCard
                        icon={CheckCircle2}
                        accent="bg-emerald-50 text-emerald-700 ring-emerald-100"
                        title={t(
                          "admin.ownerDetail.sectionInvoiceHistory",
                          "Invoice history"
                        )}
                        count={paidInvoices.length}
                        summary={
                          paidInvoices.length > 0
                            ? `${formatCents(paidCents, "EUR")} paid`
                            : t(
                                "admin.ownerDetail.nothingPaidYet",
                                "Nothing paid yet"
                              )
                        }
                        stats={[
                          {
                            label: t(
                              "admin.ownerDetail.paidTotal",
                              "Paid total"
                            ),
                            value: formatCents(paidCents, "EUR"),
                          },
                          {
                            label: t(
                              "admin.ownerDetail.paidThisYear",
                              "Paid this year"
                            ),
                            value: yearPaidCount,
                          },
                        ]}
                        help={
                          <>
                            <HelpStep
                              n={1}
                              title={t(
                                "admin.ownerDetail.historyNewestFirst",
                                "Newest first"
                              )}
                            >
                              Settled invoices land here as soon as they're
                              marked paid, most recent at the top.
                            </HelpStep>
                            <HelpStep
                              n={2}
                              title={t(
                                "admin.ownerDetail.historyPaidMeaning",
                                "Paid means proof + note"
                              )}
                            >
                              An invoice only counts as paid once a payment
                              proof and a note are recorded against it — not
                              just a status flip.
                            </HelpStep>
                            <HelpStep
                              n={3}
                              title={t(
                                "admin.ownerDetail.historyMistake",
                                "Recorded by mistake?"
                              )}
                            >
                              Unmark it to move it back to the schedule, fix the
                              proof, then mark it paid again.
                            </HelpStep>
                          </>
                        }
                        open={histOpen}
                        onToggle={() => setHistOpen((v) => !v)}
                      >
                        {paidInvoices.length === 0 ? (
                          <p className="rounded-xl border border-dashed bg-card px-3 py-6 text-center text-sm text-muted-foreground">
                            {t(
                              "admin.ownerDetail.noInvoicesPaid",
                              "No invoices paid yet."
                            )}
                          </p>
                        ) : (
                          <>
                            {/* No heldProperties here on purpose: a settled
                                invoice's own lines are a record of what was
                                billed, and the "why wasn't this billed"
                                reasons read TODAY's collection mode and room
                                occupancy. Appending them to a past month would
                                explain a months-old decision with config that
                                may have changed since. The history still
                                expands — into the invoice's real lines. */}
                            <OwnerInvoiceTable
                              groups={pagedHistory}
                              renderActions={renderActions}
                              // What the payout confirmation recorded as paid
                              // out (COL-516), and "—" where it recorded none.
                              // Not the invoice's own snapshot: it covers only
                              // the units the invoice bills, so it would
                              // understate a real payout under a column headed
                              // "Net payout" (owner-invoice-net-payout.ts).
                              netPayoutCents={recordedNetPayoutCents}
                              recordPayoutCents={() => null}
                              defaultOpen={false}
                            />
                            <Pager
                              page={histPageSafe}
                              totalPages={histTotalPages}
                              onPage={setInvPage}
                              total={historyGroups.length}
                              pageSize={SCHEDULE_GROUPS_PER_PAGE}
                            />
                          </>
                        )}
                      </CollapsibleTableCard>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* ── Apartments ── */}
              <TabsContent value="apartments" className="mt-0 space-y-3">
                {!!account?.id && (
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-lg gap-1.5"
                      onClick={() => setLinkApartmentOpen(true)}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {t("admin.ownerDetail.linkApartment", "Link apartment")}
                    </Button>
                  </div>
                )}
                {!hasBilling ? (
                  // Apartments are resolved by effective lessor, so this owner
                  // holds none — but their ACCOUNT is assigned to properties,
                  // and dropping that on the floor would read as "attached to
                  // nothing" when the Owners row says otherwise.
                  <div className="space-y-3">
                    <NoBillingRecordNote
                      text="Apartments are held by the billing record. This owner's login is assigned to the properties below, which is not the same thing as owning apartments in them."
                      onAdd={onAddBillingEntity}
                    />
                    {accountProperties.length === 0 ? (
                      <EmptyBlock
                        icon={Building}
                        text="Their login isn't assigned to any property yet."
                      />
                    ) : (
                      accountProperties.map((p) => (
                        <Link
                          key={p.id}
                          to={`/admin/properties/${p.id}`}
                          onClick={onClose}
                          className="flex items-center gap-2 rounded-xl border bg-card px-3 py-2.5 text-sm hover:border-primary/40 hover:bg-muted/40"
                        >
                          <Building className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="font-medium">{p.title ?? "—"}</span>
                          {p.city && (
                            <span className="text-muted-foreground">
                              · {p.city}
                            </span>
                          )}
                          <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
                        </Link>
                      ))
                    )}
                  </div>
                ) : isLoading ? (
                  <RowSkeleton rows={3} />
                ) : apartments.length === 0 && unassignedUnits.length === 0 ? (
                  <EmptyBlock
                    icon={Building}
                    text={t(
                      "admin.ownerDetail.noApartments",
                      "No apartments linked."
                    )}
                  />
                ) : (
                  <>
                    {pagedApartments.map((a) => (
                      <ApartmentRow
                        key={a.id}
                        apartment={a}
                        onNavigate={onClose}
                        ownerName={current?.name ?? null}
                        ownerEmail={current?.email ?? null}
                      />
                    ))}
                    {apartments.length > 0 && (
                      <Pager
                        page={aptPage}
                        totalPages={aptTotalPages}
                        onPage={setAptPage}
                        total={apartments.length}
                      />
                    )}
                    {unassignedUnits.length > 0 && (
                      <div className="space-y-3 pt-2">
                        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                          {t(
                            "admin.ownerDetail.notAssignedApartment",
                            "Not assigned to an apartment"
                          )}
                        </p>
                        {unassignedUnits.map((u) => (
                          <ApartmentRow
                            key={`prop-${u.property_id}`}
                            apartment={u}
                            onNavigate={onClose}
                            ownerName={current?.name ?? null}
                            ownerEmail={current?.email ?? null}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </TabsContent>

              <Dialog
                open={linkApartmentOpen}
                onOpenChange={(next) => {
                  if (!linkingApartment) setLinkApartmentOpen(next);
                }}
              >
                <DialogContent
                  className="max-w-lg"
                  aria-describedby={undefined}
                >
                  <DialogHeader>
                    <DialogTitle>
                      {t("admin.ownerDetail.linkApartment", "Link apartment")}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-2">
                    <ApartmentSearchPicker
                      value={null}
                      onSelect={(picked) => void linkApartment(picked)}
                      disabled={linkingApartment}
                      placeholder={t(
                        "admin.roles.createOwner.selectApartment",
                        "Search for an apartment…"
                      )}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "admin.roles.createOwner.representorDefault",
                        "Representor: Coliviq GmbH (default) — override this later from Edit Apartment."
                      )}
                    </p>
                  </div>
                </DialogContent>
              </Dialog>

              {/* ── Documents ── */}
              <TabsContent value="documents" className="mt-0 space-y-3">
                {isLoading && (
                  <p className="text-sm text-muted-foreground">
                    {t("common.loading", "Loading…")}
                  </p>
                )}
                {data?.signedDocs.map((d) => (
                  <DocRow
                    key={`signed-${d.name}-${d.url}`}
                    title={d.name}
                    subtitle={t(
                      "admin.ownerDetail.signedDocument",
                      "Signed document"
                    )}
                    value={d.url}
                  />
                ))}
                {data?.ownerDocs.map((d) => (
                  <DocRow
                    key={d.id}
                    title={d.title}
                    subtitle={`${
                      DOC_TYPES.find((t) => t.value === d.doc_type)?.label ??
                      t("admin.ownerDetail.fieldDocument", "Document")
                    } · ${formatDate(d.created_at) ?? ""}${
                      d.file_size
                        ? ` · ${(d.file_size / 1024 / 1024).toFixed(1)} MB`
                        : ""
                    }${d.notes ? ` · “${d.notes}”` : ""}`}
                    value={d.file_url}
                    onDelete={() => handleDeleteDoc(d)}
                  />
                ))}
                {!isLoading &&
                  (data?.signedDocs.length ?? 0) === 0 &&
                  (data?.ownerDocs.length ?? 0) === 0 && (
                    <EmptyBlock
                      icon={FileText}
                      text={t(
                        "admin.ownerDetail.noDocuments",
                        "No documents yet."
                      )}
                    />
                  )}

                {/* Upload — into owner_documents, keyed by the billing record.
                    Without one there is nowhere for a file to go, so the
                    dropzone is replaced rather than left to fail on drop. */}
                {!hasBilling ? (
                  <div className="pt-2">
                    <NoBillingRecordNote
                      text={t(
                        "admin.ownerDetail.noBillingRecordUpload",
                        "Owner documents are filed against the billing record. Add one to upload contracts, proofs and correspondence."
                      )}
                      onAdd={onAddBillingEntity}
                    />
                  </div>
                ) : (
                  <div className="pt-2">
                    <div className="mb-2 flex items-center gap-2">
                      <label className="text-xs text-muted-foreground">
                        {t("admin.ownerDetail.uploadAs", "Upload as")}
                      </label>
                      <select
                        value={docType}
                        onChange={(e) => setDocType(e.target.value)}
                        className="rounded-md border bg-background px-2 py-1 text-xs"
                      >
                        {DOC_TYPES.map((docTypeOpt) => (
                          <option
                            key={docTypeOpt.value}
                            value={docTypeOpt.value}
                          >
                            {docTypeOpt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* "Other" documents require a note explaining what they are. */}
                    {notesRequired && (
                      <div className="mb-3">
                        <Textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder={t(
                            "admin.ownerDetail.docs.notePlaceholder",
                            "Add a note describing this document (required for “Other”)…"
                          )}
                          rows={2}
                          className="text-sm"
                        />
                        {!notes.trim() && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            A note is required before you can upload an “Other”
                            document.
                          </p>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                      }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragging(false);
                        handleFiles(e.dataTransfer.files);
                      }}
                      className={`flex w-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed p-8 text-center transition ${
                        isDragging
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/40"
                      }`}
                    >
                      <Upload className="h-5 w-5 text-muted-foreground" />
                      <span className="text-sm">
                        <span className="font-medium">Drag &amp; drop</span> or
                        click to upload — contracts, proofs, correspondence
                      </span>
                      {upload.isPending && (
                        <span className="text-xs text-muted-foreground">
                          Uploading…
                        </span>
                      )}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        handleFiles(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </div>
                )}
              </TabsContent>

              {/* ── Internal documents ── */}
              <TabsContent value="internal-docs" className="mt-0 space-y-3">
                {!hasBilling ? (
                  // An owner note is scoped to the owner's billing record
                  // (level: "owner", lessorId), so the notes panel has nothing
                  // to open against — offering it would be a dead click.
                  <NoBillingRecordNote
                    text={t(
                      "admin.ownerDetail.noBillingRecordNotes",
                      "Internal notes and their attachments are scoped to the owner's billing record. Add one to start a note about this owner."
                    )}
                    onAdd={onAddBillingEntity}
                  />
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                        <Lock className="h-2.5 w-2.5" />
                        {t(
                          "admin.ownerDetail.adminOnlyBadge",
                          "Admin-only · never shown to the owner"
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => setNotesOpen(true)}
                        className="text-xs font-semibold text-primary underline-offset-4 hover:underline"
                      >
                        {t("admin.ownerDetail.actionOpenNotes", "Open notes")}
                      </button>
                    </div>

                    {noteFiles.length === 0 && !notesFeed.schemaReady ? (
                      // An environment without the notes migration cannot hold an
                      // owner note at all. Telling an admin to "attach a file to a
                      // note" there sends them to a panel that refuses them.
                      <EmptyBlock
                        icon={Paperclip}
                        text={t(
                          "admin.ownerDetail.notesSchemaMissing",
                          "Internal notes are not available on this environment yet — the database is missing migration 20260816130000_admin_notes_owner_scope."
                        )}
                      />
                    ) : noteFiles.length === 0 ? (
                      // Naming where these come from is the whole empty state: a
                      // bare "nothing here" would leave an admin hunting for an
                      // upload button this tab deliberately does not have.
                      <EmptyBlock
                        icon={Paperclip}
                        text={t(
                          "admin.ownerDetail.noInternalDocs",
                          "No internal documents yet. Attach a file to an internal note (the clipboard icon above) and it appears here."
                        )}
                      />
                    ) : (
                      noteFiles.map((f) => (
                        <div key={f.noteId} className="rounded-xl border p-3">
                          <p className="text-xs text-muted-foreground">
                            {formatDate(f.created_at) ?? ""}
                            {f.author?.name || f.author?.email
                              ? ` · ${f.author?.name ?? f.author?.email}`
                              : ""}
                          </p>
                          {f.body?.trim() && (
                            <p className="mt-0.5 text-sm text-foreground">
                              {f.body.trim()}
                            </p>
                          )}
                          <NoteAttachment
                            note={{
                              file_url: f.file_url,
                              file_name: f.file_name,
                              file_size: f.file_size,
                            }}
                          />
                        </div>
                      ))
                    )}
                  </>
                )}
              </TabsContent>

              {/* ── Onboarding ── */}
              <TabsContent value="onboarding" className="mt-0">
                {!hasBilling ? (
                  <NoBillingRecordNote
                    text="Onboarding is attributed to the billing record and the properties it holds, so none is on file for this owner yet."
                    onAdd={onAddBillingEntity}
                  />
                ) : submissions.length === 0 ? (
                  <EmptyBlock
                    icon={Clock}
                    text={t(
                      "admin.ownerDetail.noOnboardingSubmission",
                      "No onboarding submission on file for this owner."
                    )}
                  />
                ) : (
                  <div className="space-y-3">
                    {submissions.map((submission) => (
                      <OnboardingSubmissionCard
                        key={submission.id}
                        submission={submission}
                        // All submissions start folded; an admin opens one by
                        // clicking it. The dialog unmounts on close (Radix
                        // Dialog.Content isn't force-mounted), so each nested
                        // card's local `open` state resets to this default the
                        // next time the profile is opened.
                        defaultOpen={false}
                      >
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <SectionCard
                            title={t(
                              "admin.ownerDetail.fieldProperty",
                              "Property"
                            )}
                          >
                            <Row
                              label={t("admin.ownerDetail.fieldTitle", "Title")}
                              value={submission.propertyTitle}
                            />
                            <Row
                              label="Address"
                              value={submission.propertyAddress}
                            />
                            <Row
                              label="Management fee"
                              value={
                                // The fee is an owner/property-level arrangement,
                                // not per submission: prefer what the admin set on
                                // the owner's account (Assign → Management fee
                                // €/room/month), then the revenue-model label, then
                                // the latest invoice amount.
                                data?.stats.managementFeePerRoom != null
                                  ? `${formatEUR(data.stats.managementFeePerRoom)} / room / month`
                                  : data?.stats.revenueModelLabel &&
                                      data.stats.revenueModelLabel !== "—"
                                    ? data.stats.revenueModelLabel
                                    : data?.stats.currentMonthlyFee != null
                                      ? `${formatEUR(data.stats.currentMonthlyFee)} / month`
                                      : "—"
                              }
                            />
                          </SectionCard>
                          <SectionCard
                            title={t("admin.ownerDetail.sectionBank", "Bank")}
                          >
                            {/* Straight off THIS submission — a re-onboarding may
                                carry different bank details, so nothing here may
                                fall back to the owner-level record. */}
                            <Row
                              label={t(
                                "admin.ownerDetail.fieldAccountHolder",
                                "Account holder"
                              )}
                              value={submission.bank_holder ?? "—"}
                            />
                            <Row
                              label="IBAN"
                              value={
                                <span className="font-mono">
                                  {maskIban(submission.iban)}
                                </span>
                              }
                            />
                            <Row
                              label="BIC"
                              value={
                                <span className="font-mono">
                                  {submission.bic ?? "—"}
                                </span>
                              }
                            />
                            <Row
                              label={t(
                                "admin.ownerDetail.fieldPaymentMethod",
                                "Payment method"
                              )}
                              value={paymentMethodLabel(
                                submission.payment_method,
                                t
                              )}
                            />
                          </SectionCard>
                        </div>

                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                            {t(
                              "admin.ownerDetail.acceptedDocuments",
                              "Accepted documents"
                            )}
                          </p>
                          <div className="space-y-1.5">
                            <AcceptedRow
                              label="Management Agreement"
                              accepted={
                                submission.management_agreement_accepted
                              }
                            />
                            <AcceptedRow
                              label="Verwaltervollmacht"
                              accepted={submission.verwaltervollmacht_accepted}
                            />
                            <AcceptedRow
                              label="AGB"
                              accepted={submission.agb_accepted}
                            />
                          </div>
                        </div>

                        {/* The PDFs signed through THIS submission — an owner
                            who onboarded twice has a set per submission, so they
                            belong in the block rather than pooled owner-wide. */}
                        {submission.signedContracts.length > 0 && (
                          <div>
                            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                              {t(
                                "admin.ownerDetail.signedDocuments",
                                "Signed documents"
                              )}
                            </p>
                            <div className="space-y-1.5">
                              {submission.signedContracts.map((doc) => (
                                // A stored public URL into the contracts
                                // bucket; signed on click (COL-436).
                                <StoredFileLink
                                  key={`${doc.name}-${doc.url}`}
                                  value={doc.url}
                                  bucket="contracts"
                                  errorText={t(
                                    "admin.docs.openFailed",
                                    "Could not open the document."
                                  )}
                                  className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm transition-colors hover:bg-muted/50"
                                >
                                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                                  <span className="min-w-0 flex-1 truncate">
                                    {doc.name}
                                  </span>
                                  <Download className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                </StoredFileLink>
                              ))}
                            </div>
                          </div>
                        )}

                        {submission.status === "rejected" &&
                          submission.rejection_reason && (
                            <div className="rounded-xl border border-rose-100 bg-rose-50 p-3 text-sm text-rose-700">
                              <span className="font-semibold">
                                Rejection reason:
                              </span>{" "}
                              {submission.rejection_reason}
                            </div>
                          )}
                      </OnboardingSubmissionCard>
                    ))}

                    {/* Owner-level, so it sits outside the per-submission
                        blocks — one login link, not one per submission. */}
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleResendLogin}
                      disabled={resending || !lessor?.email}
                    >
                      <Mail className="mr-2 h-4 w-4" />
                      {resending
                        ? "Sending…"
                        : t(
                            "admin.ownerDetail.actionResendLoginLink",
                            "Resend login link"
                          )}
                    </Button>
                  </div>
                )}
              </TabsContent>

              {/* ── Subscription ── */}
              <TabsContent value="subscription" className="mt-0">
                {!hasBilling ? (
                  <NoBillingRecordNote
                    text={t(
                      "admin.ownerDetail.noBillingRecordSubscription",
                      "A subscription is held against the billing record, so this owner has none."
                    )}
                    onAdd={onAddBillingEntity}
                  />
                ) : !data?.subscription ? (
                  <EmptyBlock
                    icon={FileText}
                    text={t(
                      "admin.ownerDetail.noActiveSubscription",
                      "No active subscription."
                    )}
                  />
                ) : (
                  <SectionCard
                    title={t(
                      "admin.ownerDetail.sectionSaasSubscription",
                      "SaaS subscription"
                    )}
                  >
                    <Row
                      label={t("admin.ownerDetail.fieldPlan", "Plan")}
                      value={data.subscription.plan}
                    />
                    <Row
                      label="Status"
                      value={
                        <span className="capitalize">
                          {data.subscription.status}
                        </span>
                      }
                    />
                    <Row
                      label={t(
                        "admin.ownerDetail.fieldMonthlyMrr",
                        "Monthly (MRR)"
                      )}
                      value={formatEUR(
                        (data.subscription.amount_cents || 0) / 100
                      )}
                    />
                    <Row
                      label={t(
                        "admin.ownerDetail.fieldBillingDay",
                        "Billing day"
                      )}
                      value={data.subscription.billing_day ?? "—"}
                    />
                    <Row
                      label={t("admin.ownerDetail.fieldStarted", "Started")}
                      value={formatDate(data.subscription.started_at) ?? "—"}
                    />
                  </SectionCard>
                )}
              </TabsContent>

              {/* ── Activity ── */}
              <TabsContent value="activity" className="mt-0">
                {isLoading || invoicesLoading || auditLoading ? (
                  <RowSkeleton rows={4} />
                ) : (
                  <>
                    {auditFailed && (
                      // The logged half of the trail failed to load — an empty
                      // result here looks identical to "nothing happened yet",
                      // so say so rather than quietly showing a shorter
                      // timeline than what actually exists.
                      <p className="mb-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                        Some activity may be missing — the logged trail failed
                        to load. What's shown below is derived from invoices,
                        documents and notes only.
                      </p>
                    )}
                    <OwnerActivityTimeline events={activity} />
                  </>
                )}
              </TabsContent>
            </div>
          </Tabs>

          {/* Last child of the frame so it layers above every tab, and clipped
              to the modal rather than escaping over the page. */}
          {ownerNoteScope && (
            <NotesDrawer
              open={notesOpen}
              onClose={() => setNotesOpen(false)}
              scope={ownerNoteScope}
              emptyHint={t(
                "admin.ownerDetail.notesPlaceholder",
                "Anything the next admin should know about this owner — fee agreements, payout arrangements, calls and open follow-ups."
              )}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Why the payout details are being removed. A changed IBAN explains
          itself on the timeline — one account replaced by another. A cleared
          one says only that money can no longer be paid out, so the database
          refuses it without a reason and records the reason beside it. */}
      <AlertDialog
        open={!!bankReason}
        onOpenChange={(o) => {
          if (!o && !updateMut.isPending) setBankReason(null);
        }}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                "admin.ownerDetail.removingPayoutTitle",
                "Removing payout details"
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "admin.ownerDetail.removingPayoutBodyPrefix",
                "This clears bank details"
              )}{" "}
              {current?.name
                ? `${t("admin.ownerDetail.removingPayoutBodyFor", "for")} ${current.name} `
                : ""}
              {t(
                "admin.ownerDetail.removingPayoutBodySuffix",
                "so coliviq can no longer pay out to that account. Say why — it is recorded on this owner's Activity tab next to the removal."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            autoFocus
            rows={3}
            maxLength={500}
            value={bankReason?.text ?? ""}
            onChange={(e) =>
              setBankReason((b) => (b ? { ...b, text: e.target.value } : b))
            }
            placeholder={t(
              "admin.ownerDetail.removingPayoutPlaceholder",
              "e.g. Owner offboarded — payouts moved to the new managing agent."
            )}
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateMut.isPending}>
              {t("common.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (!bankReason?.text.trim()) return;
                saveEdit(bankReason.input, bankReason.text.trim());
              }}
              // The database refuses an empty reason too; disabling here means
              // the admin is told before the round-trip rather than after.
              disabled={!bankReason?.text.trim() || updateMut.isPending}
            >
              {updateMut.isPending
                ? t("admin.roles.editUser.saving", "Saving…")
                : t(
                    "admin.ownerDetail.actionRemoveAndRecord",
                    "Remove and record"
                  )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invoice preview / proof upload / note / delete dialogs — the same
          ones the Payments hub uses, driven by the shared row actions. */}
      {overlays}
    </>
  );
}

/** Property thumbnail that falls back to a building icon when the image is
 * missing or the URL fails to load (broken storage paths, empty covers). */
function PropertyThumb({ src, alt }: { src: string | null; alt: string }) {
  const [broken, setBroken] = useState(false);
  const showImg = src && !broken;
  return (
    <div className="flex h-14 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
      {showImg ? (
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-cover"
          loading="lazy"
          onError={() => setBroken(true)}
        />
      ) : (
        <Building2 className="h-5 w-5 text-muted-foreground" />
      )}
    </div>
  );
}

/** Per-property onboarding status pill (pending → invite sent → onboarded). */
function OnboardingStagePill({ stage }: { stage: OnboardingStage }) {
  const { t } = useLanguage();
  const map = {
    onboarded: {
      label: t("admin.ownerDetail.statusOnboarded", "Onboarded"),
      cls: "bg-emerald-50 text-emerald-700 border-emerald-100",
      dot: "bg-emerald-500",
    },
    invite_sent: {
      label: t("admin.ownerDetail.statusInviteSent", "Invite sent"),
      cls: "bg-sky-50 text-sky-700 border-sky-100",
      dot: "bg-sky-500",
    },
    pending: {
      label: t(
        "admin.ownerDetail.statusOnboardingPending",
        "Onboarding pending"
      ),
      cls: "bg-muted text-muted-foreground border-transparent",
      dot: "bg-muted-foreground/40",
    },
  } as const;
  const s = map[stage];
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${s.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

/**
 * An apartment in the owner's portfolio: the compact row (property thumbnail,
 * apartment label, the property it sits in, room count) plus a collapsible
 * holding this apartment's EFFECTIVE revenue model — its own where it sets one,
 * otherwise the property's — and its current tenants. Clicking the row toggles
 * the details; the label links to the property's Apartments tab.
 */
/**
 * Per-apartment actions. Onboarding invitations are issued PER APARTMENT (the
 * token carries apartment_id), so the send lives here rather than on the owner
 * row — an owner-level send couldn't say which apartment it was for.
 *
 * The property-level "unassigned rooms" pseudo-unit has no apartment id, so its
 * invite is property-wide; the confirm dialog says which it is.
 */
function ApartmentActionsMenu({
  apartment: p,
  ownerName,
  ownerEmail,
}: {
  apartment: OwnerApartment;
  ownerName: string | null;
  ownerEmail: string | null;
}) {
  const { t } = useLanguage();
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function sendInvite() {
    if (!ownerEmail) return;
    setBusy(true);
    try {
      const res = await inviteOwnerToProperty({
        email: ownerEmail,
        propertyId: p.property_id,
        apartmentId: p.id,
        name: ownerName,
      });
      if (!res.ok) {
        toast.error(
          res.error ??
            t(
              "admin.ownerDetail.toastInvitationFailed",
              "Could not send the onboarding invitation"
            )
        );
        return;
      }
      toast.success(
        p.id != null
          ? `Onboarding invitation sent for ${p.label}`
          : t(
              "admin.ownerDetail.toastInvitationSent",
              "Onboarding invitation sent"
            )
      );
      setConfirmOpen(false);
      // The invite creates a token, which moves this unit's onboarding stage.
      invalidateOwnerCaches(qc);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-muted-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
            aria-label={`Actions for ${p.label}`}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-60 rounded-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuItem
            className="gap-2"
            disabled={busy || !ownerEmail}
            onClick={() => setConfirmOpen(true)}
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <LinkIcon className="h-4 w-4 text-muted-foreground" />
            )}
            Send onboarding invitation
          </DropdownMenuItem>
          {!ownerEmail && (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              {t(
                "admin.ownerDetail.errNoEmailForInvite",
                "Add an email to this owner first."
              )}
            </p>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Emails a real owner — never on a single misclick. */}
      <AlertDialog
        open={confirmOpen}
        onOpenChange={(o) => !busy && setConfirmOpen(o)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                "admin.ownerDetail.confirmSendInvitationTitle",
                "Send onboarding invitation?"
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {ownerEmail} will be emailed an onboarding link
              {p.id != null ? (
                <>
                  {" "}
                  for <span className="font-medium">{p.label}</span>
                  {p.property_title ? ` (${p.property_title})` : ""}
                </>
              ) : (
                <>
                  {" "}
                  for{" "}
                  <span className="font-medium">
                    {p.property_title ?? "this property"}
                  </span>{" "}
                  — these rooms aren't in an apartment, so the invitation covers
                  the whole property
                </>
              )}
              . The link expires in 7 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>
              {t("common.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void sendInvite();
              }}
              disabled={busy}
            >
              {busy
                ? "Sending…"
                : t(
                    "admin.ownerDetail.actionSendInvitation",
                    "Send invitation"
                  )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ApartmentRow({
  apartment: p,
  onNavigate,
  ownerName,
  ownerEmail,
}: {
  apartment: OwnerApartment;
  onNavigate: () => void;
  ownerName: string | null;
  ownerEmail: string | null;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [tenantsOpen, setTenantsOpen] = useState(false);
  const { platformName, isOperator } = useOperatorBranding();
  const hasModel = p.revenue_model != null;

  // The model-specific "unit rate" line above the monthly total.
  const rateRow =
    p.revenue_model === "flat_fee_per_room" ? (
      <Row
        label={t("admin.ownerDetail.feeFlatPerRoom", "Flat fee per room")}
        value={
          p.coliviq_fee_amount != null ? formatEUR(p.coliviq_fee_amount) : "—"
        }
      />
    ) : p.revenue_model === "pct_of_rent" ? (
      <Row
        label={
          isOperator
            ? `${platformName} fee`
            : t("admin.ownerDetail.feeColiviq", "coliviq fee")
        }
        value={p.coliviq_fee_pct != null ? `${p.coliviq_fee_pct}%` : "—"}
      />
    ) : p.revenue_model === "full_rent" ? (
      <Row
        label={t("admin.ownerDetail.feeMasterRent", "Master rent")}
        value={p.master_rent != null ? formatEUR(p.master_rent) : "—"}
      />
    ) : null;

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className="flex cursor-pointer items-center gap-4 p-3 text-left hover:bg-muted/40"
      >
        <PropertyThumb
          src={p.cover_image}
          alt={p.property_title ?? "Property"}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Link
              // The apartment's own page. The property-level "unassigned rooms"
              // pseudo-unit has no apartment id, so it falls back to the property.
              to={
                p.id != null
                  ? `/admin/properties/${p.property_id}/apartments/${p.id}`
                  : `/admin/properties/${p.property_id}`
              }
              onClick={(e) => {
                e.stopPropagation();
                onNavigate();
              }}
              className={cn(
                "truncate font-medium text-primary hover:underline",
                p.unassigned && "text-muted-foreground"
              )}
            >
              {p.label}
            </Link>
            {p.floor && (
              <span className="shrink-0 text-xs text-muted-foreground">
                {p.floor}
              </span>
            )}
            <OnboardingStagePill stage={p.onboarding_stage} />
          </div>
          <p className="text-sm text-muted-foreground truncate">
            {[
              p.property_title,
              formatAddress(
                p.property_address,
                p.property_zip,
                p.property_city
              ),
            ]
              .filter(Boolean)
              .join(", ") || "—"}
          </p>
        </div>
        <Badge variant="secondary" className="shrink-0">
          {p.rooms_count} room{p.rooms_count !== 1 ? "s" : ""}
        </Badge>
        <ApartmentActionsMenu
          apartment={p}
          ownerName={ownerName}
          ownerEmail={ownerEmail}
        />
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </div>

      {open && (
        <div className="border-t bg-muted/20 px-4 py-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t("admin.ownerDetail.fieldRevenueModel", "Revenue model")}
          </p>
          {hasModel ? (
            <>
              <Row label="Revenue model" value={p.revenue_model_label} />
              <Row
                label={t(
                  "admin.ownerDetail.fieldRentCollection",
                  "Rent collection"
                )}
                value={p.collection_label}
              />
              <Row
                label={t(
                  "admin.ownerDetail.fieldNumberOfRooms",
                  "Number of rooms"
                )}
                value={p.rooms_count}
              />
              {rateRow}
              <div className="mt-1 flex items-baseline justify-between gap-4 border-t pt-2">
                <span className="text-sm font-medium">
                  {t(
                    "admin.ownerDetail.totalMonthlyRevenue",
                    "Total monthly revenue"
                  )}
                </span>
                <span
                  className={cn(
                    "text-sm font-semibold tabular-nums",
                    p.revenue_degraded && "text-amber-600"
                  )}
                  title={
                    p.revenue_degraded
                      ? "Occupancy data unavailable right now — not a verified €0"
                      : undefined
                  }
                >
                  {p.revenue_degraded ? "—" : formatEUR(p.monthly_revenue)}
                </span>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t(
                "admin.ownerDetail.noRevenueModel",
                "No revenue model set for this apartment or its property yet."
              )}
            </p>
          )}

          {/* Current tenants — collapsible; active, rent-paid occupants */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setTenantsOpen((v) => !v);
            }}
            className="mt-4 flex w-full items-center justify-between gap-2 rounded-md py-1 text-left"
          >
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("admin.ownerDetail.currentTenants", "Current tenants")}
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-foreground">
                {p.current_tenants.length}
              </span>
            </span>
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${
                tenantsOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {tenantsOpen &&
            (p.current_tenants.length === 0 ? (
              <p className="mt-1 text-sm text-muted-foreground">
                {t("admin.ownerDetail.noCurrentTenants", "No current tenants.")}
              </p>
            ) : (
              <div className="mt-2 space-y-1.5">
                {p.current_tenants.map((tenant) => (
                  <Link
                    key={tenant.bookingId}
                    to={`/admin/properties/${p.property_id}/rooms/${tenant.roomId}/tenants/${tenant.bookingId}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigate();
                    }}
                    className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2 transition-colors hover:bg-muted/50"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-600 text-[11px] font-semibold text-white">
                      {initialsFromName(tenant.name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {tenant.name}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {[tenant.roomTitle, tenant.email]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        tenant.rentStatus === "paid"
                          ? "bg-emerald-50 text-emerald-700"
                          : tenant.rentStatus === "overdue"
                            ? "bg-rose-50 text-rose-700"
                            : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {tenant.rentStatus === "paid"
                        ? t("admin.ownerDetail.rentPaid", "Rent paid")
                        : tenant.rentStatus === "overdue"
                          ? t("admin.ownerDetail.rentOverdue", "Overdue")
                          : t("admin.ownerDetail.rentPending", "Pending")}
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function Pager({
  page,
  totalPages,
  onPage,
  total,
  /**
   * Items per page. Explicit because the invoice sections page by APARTMENT
   * (each one a card of its own) while every other list still pages by row —
   * with 5 hardcoded, an apartment-paged list reported the wrong range.
   */
  pageSize = 5,
}: {
  page: number;
  totalPages: number;
  onPage: (p: number) => void;
  total: number;
  pageSize?: number;
}) {
  const { t } = useLanguage();
  if (total <= pageSize) return null;
  return (
    <div className="mt-3 flex items-center justify-between text-sm">
      <span className="text-muted-foreground">
        {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of{" "}
        {total}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1"
          disabled={page <= 1}
          onClick={() => onPage(Math.max(1, page - 1))}
        >
          <ChevronLeft className="h-4 w-4" />{" "}
          {t("admin.ownerDetail.pagePrev", "Prev")}
        </Button>
        <span className="text-muted-foreground tabular-nums">
          {page} / {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1"
          disabled={page >= totalPages}
          onClick={() => onPage(Math.min(totalPages, page + 1))}
        >
          {t("admin.ownerDetail.pageNext", "Next")}{" "}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/** Loading placeholder rows for the Properties / Invoices tabs. */
function RowSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-xl border bg-card p-3"
        >
          <div className="h-10 w-10 shrink-0 animate-pulse rounded-lg bg-muted" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-1/3 animate-pulse rounded bg-muted" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
          </div>
          <div className="h-5 w-16 shrink-0 animate-pulse rounded-full bg-muted" />
        </div>
      ))}
    </div>
  );
}

/**
 * An owner with a portal account but no `lessors` row.
 *
 * Everything this modal aggregates hangs off that row, so the tabs are honest
 * but bare. Rather than a wall of zeros that reads as "invoiced €0", each
 * affected surface says what is actually true — there is nothing to invoice
 * yet — and offers the one action that changes it.
 */
function NoBillingRecordNote({
  text,
  onAdd,
}: {
  text: string;
  onAdd?: () => void;
}) {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50/70 p-4 dark:border-amber-900/50 dark:bg-amber-950/30 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex gap-2.5">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-500" />
        <div>
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            {t("admin.ownerDetail.noBilling.heading", "No billing record yet")}
          </p>
          <p className="mt-0.5 text-sm text-amber-900/80 dark:text-amber-200/80">
            {text}
          </p>
        </div>
      </div>
      {onAdd && (
        <Button size="sm" className="shrink-0" onClick={onAdd}>
          <Building2 className="mr-1.5 h-3.5 w-3.5" />
          {t("admin.ownerDetail.noBilling.addButton", "Add billing entity")}
        </Button>
      )}
    </div>
  );
}

function EmptyBlock({
  icon: Icon,
  text,
}: {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
      <Icon className="h-6 w-6 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

function AcceptedRow({
  label,
  accepted,
}: {
  label: string;
  accepted: boolean;
}) {
  const { t } = useLanguage();
  return (
    <div className="flex items-center gap-2 text-sm">
      {accepted ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
      ) : (
        <XCircle className="h-4 w-4 text-muted-foreground" />
      )}
      <span className="font-medium">{label}</span>
      <span className="text-muted-foreground">
        {accepted
          ? t("admin.ownerDetail.docAccepted", "Accepted")
          : t("admin.ownerDetail.docMissing", "Missing")}
      </span>
    </div>
  );
}

function DocRow({
  title,
  subtitle,
  value,
  onDelete,
}: {
  title: string;
  subtitle: string;
  /** The stored value: a public URL into the contracts bucket (signed
   *  onboarding PDFs) or a bare path in it (owner_documents, the
   *  Auftragsbestätigung). Signed on click (COL-436). */
  value: string;
  onDelete?: () => void;
}) {
  const { t } = useLanguage();
  const openFailed = t("admin.docs.openFailed", "Could not open the document.");
  return (
    <div className="flex items-center gap-3 rounded-xl border bg-card p-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
        <FileText className="h-4 w-4 text-muted-foreground" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{title}</p>
        <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
      </div>
      <StoredFileLink
        value={value}
        bucket="contracts"
        errorText={openFailed}
        className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label={`View ${title}`}
        title={t("admin.ownerDetail.actionView", "View")}
      >
        <Eye className="h-4 w-4" />
      </StoredFileLink>
      <StoredFileLink
        value={value}
        bucket="contracts"
        download
        errorText={openFailed}
        className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label={`Download ${title}`}
        title={t("admin.ownerDetail.actionDownload", "Download")}
      >
        <Download className="h-4 w-4" />
      </StoredFileLink>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="rounded-lg p-2 text-muted-foreground hover:bg-rose-50 hover:text-rose-600"
          aria-label={`Delete ${title}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
